import { createIcons, Plus, FileText, Download, Sparkles, Filter, RefreshCw, Trash2, Edit, CheckCircle, Eye, Printer, Users, Calendar, Clock, Layers } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { router } from "../router/router.js";
import { platform } from "../../app/browser-platform.js";
import { type Rdo, type EnrichedRdo, type CollaboratorRdoGroup, type RdoFilterOptions } from "../../domain/rdo/rdo-domain.js";
import type { RequestContext } from "../../application/context/request-context.js";
import { PermissionCode } from "../../domain/identity/access-types.js";
import type { RdoId, ProfessionalId, UserId, ClientId } from "../../shared/kernel/identifiers.js";
import type { EnrichedProfessionalSubOsAccess } from "../../administration/admin-domain.js";
import { formatWorkOrderLabel } from "../utils/formatters.js";
import { keepFrontSelectionOnProjectChange, selectableFrontsForProject } from "../../domain/rdo/service-front-rules.js";
import { formatProjectLabel, projectOptionsForScope, resolveClientScope } from "../../domain/rdo/rdo-project-scope.js";
import { collaboratorOptions } from "../../domain/rdo/rdo-collaborator-options.js";
import {
  EMPTY_CONTEXT_FIELD,
  formatSubOsBreadcrumb,
  NO_SUB_OS_FOR_PROFESSIONAL_MESSAGE,
  type SubOsContext,
  type SubOsLike
} from "../../domain/rdo/sub-os-context.js";
import { diffRdoEntry, formatEntryFieldLabel, requiresConfirmation } from "../../domain/rdo/rdo-entry-editing.js";
import {
  NO_ENTRIES_MESSAGE,
  type CollaboratorScope,
  type RdoConsolidatedModel,
  type RdoConsolidationFilters
} from "../../domain/rdo/rdo-consolidation.js";
import "../components/document/pdf-preview.js";
import { applyLanguageSuggestions, reviewLanguage } from "../../domain/ai-review/language-rules.js";
import { CONTEXT_LOAD_STYLES, ContextLoadTracker } from "../context/context-load.js";
import { missingRestoredLevels, verifyRestoredContext, type HydrationReport } from "../context/context-hydration.js";

export class AppRdoPage extends HTMLElement {
  /**
   * Tratamento único das leituras de contexto (política em
   * presentation/context/context-load.ts). Um erro de permissão deixa de
   * virar lista vazia e de produzir "nenhum cadastro" com o cadastro
   * existente na base.
   */
  private readonly ctxLoad = new ContextLoadTracker();

  private rdos: readonly EnrichedRdo[] = [];
  private collaboratorGroups: CollaboratorRdoGroup[] = [];
  private selectedRdoIds: Set<string> = new Set();
  private isLoading: boolean = false;
  private currentView: 'grouped' | 'linear' = 'grouped';

  // Filters State
  private filters: RdoFilterOptions = {
    clientId: '',
    startDate: '',
    endDate: '',
    workOrderId: '',
    subWorkOrderId: '',
    serviceFrontId: '',
    professionalId: '',
    functionName: '',
    shift: '',
    status: ''
  };

  // Available metadata for cascaded dropdowns
  private clients: Array<{ id: string; name: string }> = [];
  private workOrders: Array<{ id: string; code: string; title: string; clientId?: string }> = [];
  private subWorkOrders: Array<{ id: string; code: string; workOrderId: string; description?: string | null; clientId?: string }> = [];
  private serviceFronts: Array<{ id: string; name: string; clientId?: string; workOrderId?: string; projectId?: string; code?: string; status?: string }> = [];
  private projects: Array<{ id: string; code: string; name: string; clientId: string }> = [];
  /** Catálogos isolados do modal: não esvaziar filtros/listas ao trocar Cliente. */
  private modalProjects: Array<{ id: string; code: string; name: string; clientId: string }> = [];
  private modalServiceFronts: Array<{ id: string; name: string; clientId?: string; workOrderId?: string; projectId?: string; code?: string; status?: string }> = [];
  /** Sub-OS do colaborador escolhido, já com a cadeia resolvida. */
  private subOsOptions: Array<{ subOs: SubOsLike; context: SubOsContext }> = [];
  /** Cadeia da Sub-OS selecionada: OS, Projeto, Cliente, Unidade/Área. */
  private subOsContext: SubOsContext | null = null;
  /** Protege seleções contra respostas antigas após mudanças rápidas no modal. */
  private modalContextRequest = 0;
  private modalWorkOrderRequest = 0;
  private subOsRequest = 0;
  /**
   * Filtros do RDO Consolidado: 1 Projeto + 1 Frente + 1 período + escopo de
   * colaborador. O escopo abre em TODOS — nunca "vazio significa todos".
   */
  private consolidation = {
    projectId: "",
    serviceFrontId: "",
    startDate: "",
    endDate: "",
    collaboratorScope: "ALL" as CollaboratorScope,
    professionalId: ""
  };
  private consolidationError: string | null = null;
  private consolidationModel: RdoConsolidatedModel | null = null;
  /** Contagem e colaboradores realmente disponíveis para os filtros atuais. */
  private consolidationScope: {
    entryCount: number;
    collaborators: { professionalId: string; name: string; functionName: string; entryCount: number }[];
  } = { entryCount: 0, collaborators: [] };
  private professionals: Array<{ id: string; name: string; functionTitle: string; registration?: string; company?: string }> = [];
  private subOsAccesses: EnrichedProfessionalSubOsAccess[] = [];

  // Editing state
  private editingRdoId: string | null = null;
  /** Vínculos gravados no apontamento que não foram achados nas listas. */
  private editHydration: HydrationReport | null = null;

  /** Desenha (ou esconde) a advertência de vínculo não reposto no formulário. */
  private renderEditHydrationNotice(): void {
    const box = this.querySelector('#rdo-form-hydration-notice') as HTMLElement | null;
    if (!box) return;
    const messages = this.editHydration?.messages ?? [];
    if (messages.length === 0) { box.style.display = 'none'; box.textContent = ''; return; }
    // textContent, não innerHTML: id de cadastro é dado, não marcação.
    box.textContent = messages.join(' ');
    box.style.display = 'block';
  }

  connectedCallback() {
    this.initData();
  }

  /**
   * Contexto da SESSÃO. Antes a tela se dava `new Set(Object.values(PermissionCode))`
   * — todas as permissões do sistema — e inventava Cliente/usuário
   * ("cli-default", "usr-default"): no teste local, qualquer perfil passava por
   * qualquer gate do RDO. No servidor o /api/rpc já remontava pela sessão.
   */
  private getContext(): RequestContext {
    const auth = contextStore.get();
    return {
      userId: auth.userId as UserId,
      activeClientId: (auth.activeClientId || undefined) as unknown as ClientId,
      permissions: auth.permissions as RequestContext["permissions"],
      professionalId: auth.professionalId as ProfessionalId || null,
      correlationId: `req-${Date.now()}` as any
    };
  }

  /**
   * Cliente realmente selecionado, ou undefined.
   *
   * getContext() usa 'cli-default' como sentinela quando não há cliente ativo.
   * Esse valor serve ao repositório de RDO, que o reconhece, mas não pode ser
   * usado para filtrar Projetos e Frentes: nenhum registro pertence a ele, e o
   * filtro devolve lista vazia.
   */
  private realClientId(): string | undefined {
    return resolveClientScope(contextStore.get().activeClientId);
  }

  /**
   * Quem pode editar apontamento. Decidido pela permissão central rdo.update —
   * nunca por comparação com o nome do papel.
   */
  private canEditEntries(): boolean {
    const permissions = contextStore.get().permissions;
    return Boolean(permissions && typeof permissions.has === 'function' && permissions.has(PermissionCode.RdoUpdate));
  }

  /** Contexto para consultas de cadastro, sem o sentinela de cliente. */
  private scopedContext(): RequestContext {
    return { ...this.getContext(), activeClientId: (this.realClientId() ?? null) as ClientId };
  }

  /** Texto de cada linha do "Resumo da seleção". */
  private consolidationSummary() {
    const project = projectOptionsForScope(this.projects, undefined).find(p => p.id === this.consolidation.projectId);
    const front = this.serviceFronts.find(f => f.id === this.consolidation.serviceFrontId);
    const br = (iso: string) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
      return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
    };

    const period = this.consolidation.startDate && this.consolidation.endDate
      ? `${br(this.consolidation.startDate)} a ${br(this.consolidation.endDate)}`
      : '—';

    let collaborators = 'Todos';
    if (this.consolidation.collaboratorScope === 'ONE') {
      const chosen = this.consolidationScope.collaborators.find(c => c.professionalId === this.consolidation.professionalId);
      collaborators = chosen ? chosen.name : 'Um colaborador (não selecionado)';
    }

    return {
      project: project ? formatProjectLabel(project) : '—',
      front: front ? `${front.code ? `${front.code} — ` : ''}${front.name}` : '—',
      period,
      collaborators,
      entryCount: String(this.consolidationScope.entryCount),
      collaboratorCount: String(this.consolidationScope.collaborators.length)
    };
  }

  private renderConsolidationSummaryItem(label: string, value: string): string {
    return `
      <div class="cons-summary-item">
        <span>${label}</span>
        <strong>${value || '—'}</strong>
      </div>`;
  }

  /**
   * Por que Visualizar/Gerar estão desabilitados. Botão apagado sem motivo é
   * um beco sem saída — a razão aparece dentro do modal.
   */
  private consolidationBlockReason(): string | null {
    const c = this.consolidation;
    if (!c.projectId) return 'Selecione o Projeto.';
    if (!c.serviceFrontId) return 'Selecione uma Frente de Serviço.';
    if (!c.startDate) return 'Informe a Data Inicial.';
    if (!c.endDate) return 'Informe a Data Final.';
    if (c.endDate < c.startDate) return 'Data Final não pode ser anterior à Data Inicial.';
    if (c.collaboratorScope === 'ONE' && !c.professionalId) return 'Selecione o colaborador.';
    if (this.consolidationScope.entryCount === 0) return NO_ENTRIES_MESSAGE;
    return null;
  }

  /** Filtros correntes no formato do serviço. */
  private consolidationFilters(): RdoConsolidationFilters {
    return {
      projectId: this.consolidation.projectId,
      serviceFrontId: this.consolidation.serviceFrontId,
      startDate: this.consolidation.startDate,
      endDate: this.consolidation.endDate,
      collaboratorScope: this.consolidation.collaboratorScope,
      ...(this.consolidation.collaboratorScope === 'ONE' ? { professionalId: this.consolidation.professionalId } : {})
    };
  }

  /** Recarrega contagem e colaboradores disponíveis para os filtros atuais. */
  private async refreshConsolidationScope() {
    try {
      this.consolidationScope = await platform.rdoService.previewConsolidationScope(this.getContext(), {
        projectId: this.consolidation.projectId,
        serviceFrontId: this.consolidation.serviceFrontId,
        startDate: this.consolidation.startDate,
        endDate: this.consolidation.endDate
      });
    } catch (e) {
      console.error('[RDO CONSOLIDADO] Falha ao contar os apontamentos dos filtros', e);
      this.consolidationScope = { entryCount: 0, collaborators: [] };
    }

    // Colaborador escolhido que deixou de ter apontamento nos novos filtros
    // não pode continuar selecionado.
    if (this.consolidation.professionalId &&
        !this.consolidationScope.collaborators.some(c => c.professionalId === this.consolidation.professionalId)) {
      this.consolidation.professionalId = '';
    }
  }

  /**
   * Pré-visualização do RDO Consolidado.
   *
   * Percorre o MESMO RdoConsolidatedModel que o builder do PDF consome, na
   * mesma ordem (Frente → Colaborador → Data → Registro), então o que aparece
   * aqui é o que sai impresso.
   */
  private renderConsolidationPreview(model: RdoConsolidatedModel): string {
    const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
    const br = (iso: string) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
      return m ? `${m[3]}/${m[2]}/${m[1]}` : "—";
    };

    return `
      <div style="margin-top: 18px; border-top: 1px solid var(--color-border); padding-top: 14px;">
        <div style="font-size: 0.72rem; letter-spacing: 0.08em; font-weight: 700; color: var(--ia-primary); margin-bottom: 8px;">
          PRÉ-VISUALIZAÇÃO — ${esc(model.header.serviceFrontName)} · ${br(model.header.periodStart)} a ${br(model.header.periodEnd)}
        </div>
        <div style="font-size: 0.76rem; color: var(--color-text-muted); margin-bottom: 12px;">
          ${model.totalCollaborators} colaborador(es) · ${model.totalRecords} registro(s) · ${model.totalDays} diária(s)
        </div>
        ${model.collaborators.map(collaborator => `
          <div style="margin-bottom: 14px; border: 1px solid var(--color-border); border-radius: 8px; padding: 10px;">
            <div style="font-weight: 700; font-size: 0.85rem; color: var(--color-text-primary);">COLABORADOR: ${esc(collaborator.name)}</div>
            <div style="font-size: 0.72rem; color: var(--color-text-muted); margin-bottom: 8px;">${esc(collaborator.functionName)}</div>
            ${collaborator.days.map(day => `
              <div style="margin-top: 8px;">
                <div style="font-size: 0.74rem; font-weight: 700; color: var(--ia-primary);">DATA: ${br(day.date)}</div>
                ${day.records.map((record, index) => `
                  <div style="font-size: 0.75rem; color: var(--color-text-primary); padding: 5px 0 5px 10px; border-left: 2px solid var(--color-border); margin-top: 5px;">
                    <div style="color: var(--color-text-muted); font-size: 0.7rem;">
                      REGISTRO ${String(index + 1).padStart(2, "0")} · ${esc(record.startTime)}–${esc(record.endTime)} · H.E. ${record.overtimeHours.toFixed(2).replace(".", ",")} · OS ${esc(record.workOrderCode)}
                    </div>
                    <div>${esc(record.description || "—")}</div>
                    ${record.inspectorComment ? `<div style="color: var(--color-text-muted); font-size: 0.7rem;">COMENTÁRIO DO FISCAL: ${esc(record.inspectorComment)}</div>` : ""}
                  </div>
                `).join("")}
              </div>
            `).join("")}
            <div style="margin-top: 10px; font-size: 0.74rem; color: var(--color-text-muted);">
              TOTAIS DO COLABORADOR — DIÁRIAS: ${collaborator.totalDays} · HORAS EXTRA: ${collaborator.totalOvertimeHours.toFixed(2).replace(".", ",")}
            </div>
          </div>
        `).join("")}
      </div>`;
  }

  /** Mensagem do seletor de Sub-OS conforme o estado do colaborador. */
  private subOsMessage(): string {
    const professionalId = (this.querySelector('#form-professional') as HTMLSelectElement | null)?.value || '';
    if (!professionalId) return 'Selecione primeiro o Colaborador';
    return this.subOsOptions.length === 0
      ? NO_SUB_OS_FOR_PROFESSIONAL_MESSAGE
      : 'Selecione a Sub-OS';
  }

  /** Item somente-leitura do bloco de contexto automático. */
  private renderAutoContextItem(label: string, value: string | null | undefined): string {
    const text = value && value !== EMPTY_CONTEXT_FIELD ? value : EMPTY_CONTEXT_FIELD;
    return `
      <div class="auto-context-item">
        <span>${label}</span>
        <strong>${text}</strong>
      </div>`;
  }

  /**
   * Trilha OS > Projeto > Sub-OS do contexto resolvido. O contêiner existe
   * sempre, para refreshAutoContext conseguir preenchê-lo depois.
   */
  private renderSubOsBreadcrumb(): string {
    const trail = this.subOsContext ? formatSubOsBreadcrumb(this.subOsContext) : '';
    return `<div class="sub-os-breadcrumb">${trail}</div>`;
  }

  /**
   * Recarrega as Sub-OS do colaborador escolhido.
   *
   * Trocar o colaborador limpa a Sub-OS anterior: uma Sub-OS de outro
   * profissional nunca pode continuar selecionada.
   */

  private showRdoFormError(message: string) {
    const banner = this.querySelector('#rdo-form-error-banner') as HTMLElement | null;
    if (banner) { banner.textContent = message; banner.style.display = 'block'; }
  }

  /** Dados carregados no Cliente selecionado NO RDO, independentemente da sessão. */
  private async loadModalClientCatalogues(clientId: string) {
    const request = ++this.modalContextRequest;
    if (!clientId) { this.modalProjects = []; this.modalServiceFronts = []; return; }
    const ctx = { ...this.getContext(), activeClientId: clientId as ClientId };
    try {
      const [projects, fronts] = await Promise.all([
        platform.projectService.listProjects(clientId, undefined, ctx),
        platform.serviceFrontService.listFronts(ctx, { includeInactive: true })
      ]);
      if (request !== this.modalContextRequest ||
          (this.querySelector('#form-client') as HTMLSelectElement | null)?.value !== clientId) return;
      this.modalProjects = projects.filter(p => p.clientId === clientId).map(p => ({
        id: p.id, code: p.code, name: p.name, clientId: p.clientId
      }));
      this.modalServiceFronts = fronts.filter(f => f.clientId === clientId).map(f => ({
        id: f.id, name: f.name, clientId: f.clientId, workOrderId: '',
        projectId: f.projectId ?? '', code: f.code ?? '', status: f.status
      }));
    } catch (error) {
      if (request !== this.modalContextRequest) return;
      this.modalProjects = []; this.modalServiceFronts = [];
      console.error('[RDO] Erro ao carregar Projetos/Frentes:', error);
      this.showRdoFormError('Falha ao consultar Projetos/Frentes deste Cliente. Confira os vínculos e permissões.');
    }
  }

  /** Seletor de Projeto real, restringido ao Cliente escolhido. */
  private refreshModalProjectOptions(selectedProjectId: string | null = null) {
    const clientId = (this.querySelector('#form-client') as HTMLSelectElement | null)?.value || '';
    const select = this.querySelector('#form-project') as HTMLSelectElement | null;
    if (!select) return;
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
    const list = clientId ? projectOptionsForScope(this.modalProjects, clientId) : [];
    select.disabled = !clientId || list.length === 0;
    select.innerHTML = '<option value="">' + (!clientId ? 'Selecione primeiro o Cliente...' : 'Selecione o Projeto...') + '</option>'
      + list.map(p => `<option value="${esc(p.id)}">${esc(formatProjectLabel(p))}</option>`).join('');
    select.value = selectedProjectId && list.some(p => p.id === selectedProjectId) ? selectedProjectId : '';
  }

  /** A OS deve pertencer ao Cliente e estar formalmente vinculada ao Projeto. */
  private async refreshModalWorkOrderOptions(selectedWoId: string | null = null) {
    const request = ++this.modalWorkOrderRequest;
    const clientId = (this.querySelector('#form-client') as HTMLSelectElement | null)?.value || '';
    const projectId = (this.querySelector('#form-project') as HTMLSelectElement | null)?.value || '';
    const select = this.querySelector('#form-work-order') as HTMLSelectElement | null;
    if (!select) return;
    select.disabled = true;
    select.innerHTML = '<option value="">' + (!projectId ? 'Selecione primeiro o Projeto...' : 'Carregando OS vinculadas...') + '</option>';
    if (!clientId || !projectId) return;
    try {
      const ctx = { ...this.getContext(), activeClientId: clientId as ClientId };
      const workOrders = await platform.adminService.listWorkOrdersForDocument(ctx, clientId, projectId);
      if (request !== this.modalWorkOrderRequest ||
          (this.querySelector('#form-client') as HTMLSelectElement | null)?.value !== clientId ||
          (this.querySelector('#form-project') as HTMLSelectElement | null)?.value !== projectId) return;
      const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
      const available = workOrders.filter(w => w.clientId === clientId);
      select.disabled = available.length === 0;
      select.innerHTML = '<option value="">Selecione a OS...</option>' +
        available.map(w => `<option value="${esc(w.id)}">${esc(formatWorkOrderLabel(w))}</option>`).join('');
      select.value = selectedWoId && available.some(w => w.id === selectedWoId) ? selectedWoId : '';
    } catch (error) {
      if (request !== this.modalWorkOrderRequest) return;
      console.error('[RDO] Erro ao carregar OS por Projeto:', error);
      this.showRdoFormError('Não foi possível carregar OS vinculadas ao Projeto selecionado.');
      select.innerHTML = '<option value="">OS indisponível</option>';
    }
  }

  /** Sub-OS autorizadas pelo backend para o Professional e Cliente locais. */
  private async reloadSubOsForProfessional(preserveSubOsId: string | null = null) {
    const professionalId = (this.querySelector('#form-professional') as HTMLSelectElement | null)?.value || '';
    const clientId = (this.querySelector('#form-client') as HTMLSelectElement | null)?.value || '';
    const request = ++this.subOsRequest;
    if (!professionalId || !clientId) { this.subOsOptions = []; this.subOsContext = null; return; }
    try {
      const ctx = { ...this.getContext(), activeClientId: clientId as ClientId };
      const options = await platform.rdoService.listSubOsForProfessional(ctx, professionalId);
      if (request !== this.subOsRequest ||
          (this.querySelector('#form-professional') as HTMLSelectElement | null)?.value !== professionalId ||
          (this.querySelector('#form-client') as HTMLSelectElement | null)?.value !== clientId) return;
      this.subOsOptions = options.filter(o => o.context.clientId === clientId);
      this.subOsContext = this.subOsOptions.find(o => o.subOs.id === preserveSubOsId)?.context ?? null;
    } catch (error) {
      if (request !== this.subOsRequest) return;
      console.error('[RDO] Falha ao carregar Sub-OS autorizadas:', error);
      this.subOsOptions = []; this.subOsContext = null;
      this.showRdoFormError('Não foi possível carregar as Sub-OS atribuídas ao colaborador.');
    }
  }


  /** Recompõe Sub-OS somente quando Cliente, Projeto, OS e Colaborador coincidem. */
  private refreshSubOsSelect(selectedSubOsId: string | null = null) {
    const select = this.querySelector('#form-sub-work-order') as HTMLSelectElement | null;
    if (!select) return;
    const clientId = (this.querySelector('#form-client') as HTMLSelectElement | null)?.value || '';
    const projectId = (this.querySelector('#form-project') as HTMLSelectElement | null)?.value || '';
    const woId = (this.querySelector('#form-work-order') as HTMLSelectElement | null)?.value || '';
    const professionalId = (this.querySelector('#form-professional') as HTMLSelectElement | null)?.value || '';
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
    const available = clientId && projectId && woId && professionalId
      ? this.subOsOptions.filter(o => o.context.clientId === clientId
          && o.context.projectId === projectId && o.context.workOrderId === woId)
      : [];
    select.disabled = available.length === 0;
    select.innerHTML = '<option value="">' + (!clientId ? 'Selecione primeiro o Cliente...' :
      !projectId ? 'Selecione primeiro o Projeto...' : !woId ? 'Selecione primeiro a OS...' :
      !professionalId ? 'Selecione o Colaborador...' : available.length === 0 ?
      'Nenhuma Sub-OS atribuída nesta OS/Projeto' : 'Selecione a Sub-OS...') + '</option>' +
      available.map(o => `<option value="${esc(o.subOs.id)}">${esc(o.subOs.code)}
        ${o.subOs.description ? ' — ' + esc(o.subOs.description) : ''}</option>`).join('');
    select.value = selectedSubOsId && available.some(o => o.subOs.id === selectedSubOsId) ? selectedSubOsId : '';
    if (!select.value) this.subOsContext = null;
  }

  /** Unidade só pede seleção adicional quando há várias opções reais. */
  private refreshModalUnitArea(selectedUnitId: string | null = null) {
    const wrapper = this.querySelector('#rdo-unit-area-slot') as HTMLElement | null;
    if (!wrapper) return;
    const context = this.subOsContext;
    const options = context && !context.unitAreaId ? context.unitAreaOptions : [];
    if (options.length <= 1) { wrapper.innerHTML = ''; return; }
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
    wrapper.innerHTML = `<div class="form-group"><label for="form-unit-area">Unidade / Área *</label>
      <select id="form-unit-area" required><option value="">Selecione a Unidade/Área...</option>
      ${options.map(o => `<option value="${esc(o.id)}">${esc(o.code)} — ${esc(o.name)}</option>`).join('')}</select>
      <span class="form-hint">Selecione a unidade vinculada ao Projeto.</span></div>`;
    const selected = this.querySelector('#form-unit-area') as HTMLSelectElement | null;
    if (selected && selectedUnitId && options.some(o => o.id === selectedUnitId)) selected.value = selectedUnitId;
  }

  /** Atualiza o bloco de contexto automático com a cadeia da Sub-OS. */
  private refreshAutoContext() {
    const breadcrumb = this.querySelector('.sub-os-breadcrumb') as HTMLElement | null;
    if (breadcrumb) breadcrumb.textContent = this.subOsContext ? formatSubOsBreadcrumb(this.subOsContext) : '';

    const container = this.querySelector('.auto-context') as HTMLElement | null;
    if (!container) return;

    container.innerHTML = [
      this.renderAutoContextItem('OS', this.subOsContext?.workOrderCode),
      this.renderAutoContextItem('Projeto', this.subOsContext?.projectLabel),
      this.renderAutoContextItem('Cliente', this.subOsContext?.clientName),
      this.renderAutoContextItem('Unidade / Área', this.subOsContext?.unitAreaLabel)
    ].join('');
  }


  /** Sub-OS nunca reescreve Cliente, Projeto e OS escolhidos na interface. */
  private applySubOsContextToForm() {
    this.refreshModalUnitArea();
  }

  /**
   * Opções de Frente no formulário do apontamento: só as ATIVAS do Projeto
   * escolhido. Se a Frente já selecionada não pertence ao novo Projeto, a
   * seleção é limpa — Projeto A com Frente do Projeto B nunca é aceito.
   */
  private refreshFrontOptions(selectedFrontId: string | null) {
    const projectSelect = this.querySelector('#form-project') as HTMLSelectElement | null;
    const frontSelect = this.querySelector('#form-service-front') as HTMLSelectElement | null;
    if (!frontSelect) return;

    const projectId = projectSelect?.value || '';
    const candidates = this.modalServiceFronts.map(f => ({ ...f, projectId: f.projectId ?? null, status: f.status ?? 'ACTIVE' }));
    const available = selectableFrontsForProject(candidates, projectId);
    const keptId = keepFrontSelectionOnProjectChange(candidates, projectId, selectedFrontId ?? frontSelect.value ?? '');

    frontSelect.disabled = !projectId;
    frontSelect.innerHTML = `
      <option value="">${projectId ? 'Selecione a Frente...' : 'Selecione primeiro o Projeto...'}</option>
      ${available.map(f => `<option value="${f.id}" ${f.id === keptId ? 'selected' : ''}>${f.code ? `${f.code} — ` : ''}${f.name}</option>`).join('')}
    `;
    frontSelect.value = keptId;
  }

  private async initData() {
    this.isLoading = true;
    this.render();

    try {
      const ctx = this.getContext();

      // Load metadata from Admin & Identity Repositories
      if (platform.adminService) {
        const cls = await this.ctxLoad.load("Clientes", () => platform.adminService.listAuthorizedClients(ctx));
        this.clients = cls.map((c: any) => ({ id: c.id, name: c.name || c.legalName }));

        const wos = await this.ctxLoad.load("Ordens de Serviço", () => platform.adminService.listWorkOrdersForDocument(ctx));
        this.workOrders = wos.map(w => ({ id: w.id, code: w.code, title: w.description || w.code, clientId: w.clientId }));

        const swos = await this.ctxLoad.load("Sub-OS", () => platform.adminService.listSubWorkOrdersForDocument(ctx));
        this.subWorkOrders = swos.map(s => ({ id: s.id, code: s.code, workOrderId: s.workOrderId, description: s.description, clientId: s.clientId }));

        // As Frentes vêm do ServiceFrontService, não do listing cru do Admin:
        // é ele que resolve o Projeto de uma Frente legada (cadastrada presa a
        // uma OS antes do vínculo com Projeto). Lendo o cru, essas Frentes
        // ficavam sem projectId e não apareciam sob NENHUM Projeto — junto com
        // todos os apontamentos delas.
        //
        // O cliente também precisa ser o REAL: com o sentinela 'cli-default' o
        // repositório filtra por um cliente que não existe e devolve zero.
        const fronts = await platform.serviceFrontService
          .listFronts(this.scopedContext(), { includeInactive: true })
          .catch((e: any) => {
            console.error('[RDO] Falha ao carregar Frentes de Serviço', e);
            return [];
          });
        this.serviceFronts = fronts.map(f => ({
          id: f.id,
          name: f.name,
          clientId: f.clientId,
          workOrderId: '',
          projectId: f.projectId ?? '',
          code: f.code ?? '',
          status: f.status
        }));

        // COLABORADOR = PROFESSIONAL, do cadastro oficial de Profissionais.
        //
        // Precisa do cliente REAL: com o sentinela 'cli-default' o repositório
        // filtra por um cliente inexistente e devolve zero — era isso que
        // deixava o seletor vazio. Sem cliente ativo, lista-se o cadastro.
        const profs = await platform.adminService.listProfessionals(this.scopedContext()).catch((e: any) => {
          console.error('[RDO] Falha ao carregar os Profissionais cadastrados', e);
          return [];
        });
        // REGRA V4: PROFESSIONAL É GLOBAL. Após a globalização, listProfessionals
        // retorna TODOS os profissionais. O RDO deve filtrar apenas os elegíveis
        // como Inspetor (Inspector + NdtInspector ativos), reutilizando a mesma
        // regra oficial do Controle de Campo (ELIGIBLE_INSPECTOR_TYPES).
        const eligibleTypes = new Set(["INSPECTOR", "NDT_INSPECTOR"]);
        const eligibleProfs = (profs as any[]).filter(p =>
          p.status === "ACTIVE" && eligibleTypes.has(String(p.professionalType ?? "").trim())
        );
        // Ativos, em ordem alfabética, com a função vinda do Professional —
        // nunca do papel de acesso do User, e sem preencher com "INSPETOR"
        // quando o cadastro não tem função.
        this.professionals = collaboratorOptions(eligibleProfs as any).map(option => ({
          id: option.professionalId,
          name: option.name,
          functionTitle: option.functionTitle,
          registration: option.registration,
          company: option.company
        }));

        this.subOsAccesses = await platform.adminService.listProfessionalSubOsAccesses(ctx).catch(() => []);
      }

      // Projetos: é por eles que a Frente de Serviço é escolhida.
      //
      // O cliente precisa ser o REAL. getContext() substitui a ausência de
      // cliente pelo sentinela 'cli-default', e filtrar os Projetos por ele
      // devolvia zero — era isso que deixava o seletor vazio. Sem cliente
      // escolhido, lista-se o cadastro inteiro.
      if (platform.projectService) {
        try {
          const projects = await platform.projectService.listProjects(this.realClientId(), undefined, ctx);
          this.projects = projects.map((p: any) => ({ id: p.id, code: p.code, name: p.name, clientId: p.clientId }));
        } catch (e) {
          console.error('[RDO] Falha ao carregar os Projetos cadastrados', e);
          this.projects = [];
        }
      }

      await this.loadRdos();
    } catch (e: any) {
      console.error("Error loading RDO data:", e);
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private async loadRdos() {
    const ctx = this.getContext();
    if (!platform.rdoService) return;

    // Load enriched list and collaborator groups
    this.rdos = await platform.rdoService.getEnrichedRdos(ctx, this.filters);
    this.collaboratorGroups = await platform.rdoService.getCollaboratorGroups(ctx, this.filters);
  }

  private render() {
    const totalApontamentos = this.rdos.length;
    const totalDiarias = new Set(this.rdos.map(r => `${r.professionalId}_${r.date}`)).size;
    const totalHorasNormais = this.rdos.reduce((acc, r) => acc + (r.normalHours || 0), 0);
    const totalHorasExtras = this.rdos.reduce((acc, r) => acc + (r.overtimeHours || 0), 0);
    const totalColaboradores = new Set(this.rdos.map(r => r.professionalId)).size;

    // Filter available sub work orders if workOrderId filter is selected
    const filteredSubWos = this.filters.workOrderId 
      ? this.subWorkOrders.filter(s => s.workOrderId === this.filters.workOrderId)
      : this.subWorkOrders;

    this.innerHTML = `
      <style>${CONTEXT_LOAD_STYLES}</style>
      ${this.ctxLoad.banner()}
      <style>
        .rdo-container {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
          color: var(--color-text, var(--color-bg-primary));
        }
        .rdo-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .rdo-title-group h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .rdo-title-group p {
          font-size: 0.875rem;
          color: var(--color-graphite-400);
          margin: 0;
        }
        .rdo-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .btn-rdo {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.15s ease;
        }
        .btn-rdo-primary {
          background: var(--ia-action-bg, var(--color-primary));
          color: var(--ia-action-fg, #F7F8F8);
        }
        .btn-rdo-primary:hover {
          background-color: var(--color-primary);
        }
        .btn-rdo-secondary {
          background-color: var(--color-primary);
          /* Era --color-bg-secondary: texto escuro sobre o fundo escuro. */
          color: var(--color-text-primary);
          border: 1px solid var(--color-graphite-700);
        }
        .btn-rdo-secondary:hover {
          background-color: var(--color-text-muted);
        }
        .btn-rdo-success {
          background-color: var(--status-success-text);
          color: var(--color-text-primary);
        }
        .btn-rdo-success:hover {
          background-color: var(--status-success-text);
        }
        
        /* METRICS BAR */
        .metrics-bar {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }
        .metric-card {
          background: var(--color-graphite-800);
          border: 1px solid var(--color-primary);
          border-radius: 8px;
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .metric-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-graphite-400);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .metric-val {
          font-size: 1.35rem;
          font-weight: 700;
          /* Era --color-bg-primary: o valor tinha a cor do próprio fundo. */
          color: var(--color-text-primary);
        }
        .metric-val.accent {
          color: var(--color-text-muted);
        }
        .metric-val.green {
          color: var(--status-success-text);
        }
        .metric-val.amber {
          color: var(--status-warning-text);
        }

        /* FILTERS TOOLBAR */
        .filters-panel {
          background: var(--color-graphite-800);
          border: 1px solid var(--color-primary);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 24px;
        }
        .filters-title {
          font-size: 0.875rem;
          font-weight: 700;
          color: var(--color-graphite-300);
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
        }
        .filter-control {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .filter-control label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-graphite-400);
        }
        .filter-control input,
        .filter-control select {
          background: var(--color-primary);
          border: 1px solid var(--color-graphite-700);
          color: var(--color-text-primary);
          padding: 6px 10px;
          border-radius: 4px;
          font-size: 0.825rem;
          outline: none;
        }
        .filter-control input:focus,
        .filter-control select:focus {
          border-color: var(--color-text-muted);
        }
        .filters-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          margin-top: 14px;
          padding-top: 10px;
          border-top: 1px solid var(--color-primary);
        }

        /* TABS AND VIEW SWITCHER */
        .view-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .view-tabs {
          display: flex;
          background: var(--color-graphite-800);
          padding: 3px;
          border-radius: 6px;
          border: 1px solid var(--color-primary);
        }
        .view-tab {
          padding: 6px 14px;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          background: transparent;
          color: var(--color-graphite-400);
          transition: all 0.15s;
        }
        .view-tab.active {
          background: var(--color-primary);
          color: var(--color-text-muted);
        }

        /* COLLABORATOR CARDS (OFICIAL GROUPED VIEW) */
        .collab-card {
          background: var(--color-graphite-800);
          border: 1px solid var(--color-primary);
          border-radius: 8px;
          margin-bottom: 18px;
          overflow: hidden;
        }
        .collab-card-header {
          background: var(--color-primary);
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid var(--color-graphite-700);
          flex-wrap: wrap;
          gap: 10px;
        }
        .collab-name-badge {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .collab-name-badge strong {
          font-size: 0.95rem;
          color: var(--color-bg-primary);
        }
        .collab-name-badge span {
          font-size: 0.8rem;
          color: var(--color-graphite-400);
          background: var(--color-graphite-800);
          padding: 2px 8px;
          border-radius: 4px;
          border: 1px solid var(--color-graphite-700);
        }
        .collab-stats-badges {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        .stat-badge {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-graphite-300);
        }
        .stat-badge strong {
          color: var(--color-text-muted);
        }

        /* DATA TABLES */
        .rdo-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.825rem;
          text-align: left;
        }
        .rdo-table th {
          background: var(--color-graphite-800);
          color: var(--color-graphite-400);
          font-weight: 600;
          padding: 10px 12px;
          border-bottom: 1px solid var(--color-primary);
          text-transform: uppercase;
          font-size: 0.7rem;
        }
        .rdo-table td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--color-primary);
          color: var(--color-graphite-200);
        }
        .rdo-table tr:hover td {
          background: rgba(30, 41, 59, 0.5);
        }
        .status-pill {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 700;
          display: inline-block;
        }
        .status-draft { background: var(--color-graphite-700); color: var(--color-graphite-300); }
        .status-issued { background: var(--status-success-solid); color: var(--color-primary-text); }
        .status-cancelled { background: var(--ia-danger-bg, #7f1d1d); color: #FFFFFF; }

        /* MODAL STYLES */
        .rdo-modal {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(4px);
          z-index: 1000;
          display: none;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .rdo-modal.active {
          display: flex;
        }
        /* --- Novo Apontamento: Responsável → Contexto automático → Apontamento --- */
        .form-section-title {
          font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase;
          font-weight: 700; color: var(--ia-fg-soft, #AEAEAE); margin: 18px 0 10px 0;
          padding-bottom: 6px; border-bottom: 1px solid #5C5C5C;
        }
        .form-section-title:first-child { margin-top: 0; }
        .form-hint { display: block; font-size: 0.72rem; color: var(--ia-fg-muted, #909090); margin-top: 5px; }
        .sub-os-breadcrumb { font-size: 0.76rem; color: var(--ia-fg-soft, #AEAEAE); margin-bottom: 10px; font-weight: 600; }
        .auto-context {
          display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px;
          background: var(--ia-bg-raised, #383838); border: 1px solid #5C5C5C; border-radius: 8px; padding: 14px;
        }
        .auto-context-item span { display: block; font-size: 0.7rem; color: var(--ia-fg-muted, #909090); }
        .auto-context-item strong { display: block; font-size: 0.85rem; color: var(--ia-fg, #FFFFFF); margin-top: 3px; font-weight: 600; }
        @media (max-width: 820px) { .auto-context { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

        /* --- Modal Gerar RDO Consolidado: blocos, respiro e contraste --- */
        .cons-box { max-width: 940px; }
        .cons-subtitle { margin: 3px 0 0 0; font-size: 0.8rem; color: var(--ia-fg-soft, #AEAEAE); font-weight: 400; }
        .cons-close { background: none; border: none; color: var(--ia-fg-muted, #909090); font-size: 1.5rem; line-height: 1; cursor: pointer; padding: 0 4px; }
        .cons-close:hover { color: var(--ia-fg, #EEEEEE); }
        .cons-body { padding: 20px 24px; overflow-y: auto; }
        .cons-section { padding-bottom: 18px; margin-bottom: 18px; border-bottom: 1px solid #5C5C5C; }
        .cons-section-title { font-size: 0.76rem; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; color: var(--ia-fg-soft, #AEAEAE); margin-bottom: 12px; }
        .cons-grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
        .cons-field { display: flex; flex-direction: column; }
        .cons-field label { font-size: 0.78rem; font-weight: 600; color: var(--ia-fg, #EEEEEE); margin-bottom: 6px; }
        .cons-field select, .cons-field input {
          width: 100%; box-sizing: border-box; padding: 10px 12px;
          background: var(--ia-bg-raised, #383838); border: 1px solid #5C5C5C; border-radius: 6px;
          color: var(--ia-fg, #FFFFFF); font-size: 0.88rem;
        }
        .cons-field select:disabled, .cons-field input:disabled { color: var(--ia-fg-muted, #909090); background: var(--ia-bg-raised, #383838); cursor: not-allowed; }
        .cons-field select:focus, .cons-field input:focus { outline: none; border-color: #AEAEAE; }
        .cons-field option { background: var(--ia-bg-raised, #383838); color: var(--ia-fg, #FFFFFF); }
        .cons-hint { font-size: 0.74rem; color: var(--ia-fg-muted, #909090); margin-top: 6px; }
        .cons-link { background: none; border: none; color: var(--ia-fg, #EEEEEE); cursor: pointer; padding: 0; font-size: 0.74rem; text-decoration: underline; }
        .cons-radio-group { display: flex; gap: 24px; flex-wrap: wrap; }
        .cons-radio { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 0.88rem; color: var(--ia-fg, #EEEEEE); }
        .cons-radio input { width: 16px; height: 16px; accent-color: #AEAEAE; cursor: pointer; }
        .cons-summary { background: var(--ia-bg-raised, #383838); border: 1px solid #5C5C5C; border-radius: 8px; padding: 16px; }
        .cons-summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
        .cons-summary-item span { display: block; font-size: 0.72rem; color: var(--ia-fg-muted, #909090); }
        .cons-summary-item strong { display: block; font-size: 0.86rem; color: var(--ia-fg, #FFFFFF); margin-top: 3px; font-weight: 600; }
        .cons-error { margin-top: 16px; padding: 11px 14px; border-radius: 6px; background: #b71c1c; color: #ffebee; font-size: 0.84rem; }
        .cons-blocked { margin-top: 16px; padding: 11px 14px; border-radius: 6px; background: #5C5C5C; color: var(--ia-on-color, #EEEEEE); font-size: 0.84rem; }
        .btn-rdo:disabled { opacity: 0.45; cursor: not-allowed; }
        @media (max-width: 820px) {
          .cons-grid-2, .cons-summary-grid { grid-template-columns: 1fr; }
        }

        .rdo-modal-box {
          background: var(--color-graphite-800);
          border: 1px solid var(--color-graphite-700);
          border-radius: 10px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);
          overflow: hidden;
        }
        .rdo-modal-header {
          padding: 16px 20px;
          background: var(--color-primary);
          border-bottom: 1px solid var(--color-graphite-700);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .rdo-modal-header h2 {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0;
        }
        .rdo-modal-body {
          padding: 20px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .form-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        .form-grid-3 {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 14px;
        }
        .form-grid-4 {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1fr;
          gap: 12px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .form-group label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-graphite-400);
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          background: var(--color-primary);
          border: 1px solid var(--color-graphite-700);
          color: var(--color-text-primary);
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 0.875rem;
          outline: none;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          border-color: var(--color-text-muted);
        }
        .rdo-modal-footer {
          padding: 14px 20px;
          background: var(--color-primary);
          border-top: 1px solid var(--color-graphite-700);
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        /* FULLSCREEN PREVIEW MODAL */
        .pdf-modal-box {
          background: var(--color-graphite-800);
          border: 1px solid var(--color-graphite-700);
          border-radius: 8px;
          width: 95vw;
          height: 92vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
      </style>

      <div class="rdo-container">
        <!-- HEADER -->
        <div class="rdo-header">
          <div class="rdo-title-group">
            <h1><i data-lucide="file-text" style="color: var(--color-text-muted); width: 26px; height: 26px;"></i> Relatórios Diários de Obra (RDO)</h1>
            <p>Apontamento analítico de horas, rastreamento operacional e emissão oficial de RDO Individual e Consolidado</p>
          </div>
          <div class="rdo-actions">
            <button class="btn-rdo btn-rdo-secondary" id="btn-open-consolidado">
              <i data-lucide="layers" style="width: 16px; height: 16px; color: var(--color-text-muted);"></i> Gerar RDO Consolidado
            </button>
            <button class="btn-rdo btn-rdo-primary" id="btn-open-create">
              <i data-lucide="plus" style="width: 16px; height: 16px;"></i> Novo Apontamento (RDO)
            </button>
          </div>
        </div>

        <!-- METRICS BAR -->
        <div class="metrics-bar">
          <div class="metric-card">
            <span class="metric-label">Total Apontamentos</span>
            <span class="metric-val accent">${totalApontamentos}</span>
          </div>
          <div class="metric-card">
            <span class="metric-label">Diárias Únicas</span>
            <span class="metric-val green">${totalDiarias}</span>
          </div>
          <div class="metric-card">
            <span class="metric-label">Horas Normais</span>
            <span class="metric-val">${totalHorasNormais.toFixed(1)} h</span>
          </div>
          <div class="metric-card">
            <span class="metric-label">Horas Extras</span>
            <span class="metric-val amber">${totalHorasExtras.toFixed(1)} h</span>
          </div>
          <div class="metric-card">
            <span class="metric-label">Colaboradores</span>
            <span class="metric-val">${totalColaboradores}</span>
          </div>
        </div>

        <!-- HIERARCHICAL FILTERS PANEL -->
        <div class="filters-panel">
          <div class="filters-title">
            <i data-lucide="filter" style="width: 16px; height: 16px; color: var(--color-text-muted);"></i>
            Filtros Hierárquicos Operacionais
          </div>
          <div class="filters-grid">
            <div class="filter-control">
              <label>Cliente</label>
              <select id="filter-client">
                <option value="">Todos os Clientes</option>
                ${this.clients.map(c => `<option value="${c.id}" ${this.filters.clientId === c.id ? 'selected' : ''}>${c.name}</option>`).join('')}
              </select>
            </div>
            <div class="filter-control">
              <label>Data Início</label>
              <input type="date" id="filter-start-date" value="${this.filters.startDate || ''}" />
            </div>
            <div class="filter-control">
              <label>Data Fim</label>
              <input type="date" id="filter-end-date" value="${this.filters.endDate || ''}" />
            </div>
            <div class="filter-control">
              <label>OS (Ordem de Serviço)</label>
              <select id="filter-os">
                <option value="">Todas as OS</option>
                ${(this.filters.clientId ? this.workOrders.filter(w => !w.clientId || w.clientId === this.filters.clientId) : this.workOrders).map(w => `<option value="${w.id}" ${this.filters.workOrderId === w.id ? 'selected' : ''}>${formatWorkOrderLabel(w)}</option>`).join('')}
              </select>
            </div>
            <div class="filter-control">
              <label>Sub-OS</label>
              <select id="filter-sub-os">
                <option value="">Todas as Sub-OS</option>
                ${filteredSubWos.map(s => `<option value="${s.id}" ${this.filters.subWorkOrderId === s.id ? 'selected' : ''}>${s.code}</option>`).join('')}
              </select>
            </div>
            <div class="filter-control">
              <label>Projeto</label>
              <select id="filter-project">
                <option value="">Todos os Projetos</option>
                ${(this.filters.clientId ? this.projects.filter(p => p.clientId === this.filters.clientId) : this.projects)
                  .map(p => `<option value="${p.id}" ${this.filters.projectId === p.id ? 'selected' : ''}>${p.code} — ${p.name}</option>`).join('')}
              </select>
            </div>
            <div class="filter-control">
              <label>Frente de Serviço</label>
              <select id="filter-front">
                <option value="">${this.filters.projectId ? 'Todas as Frentes do Projeto' : 'Todas as Frentes'}</option>
                ${(this.filters.projectId
                    ? this.serviceFronts.filter(f => f.projectId === this.filters.projectId)
                    : this.serviceFronts
                  ).map(f => `<option value="${f.id}" ${this.filters.serviceFrontId === f.id ? 'selected' : ''}>${f.code ? `${f.code} — ` : ''}${f.name}</option>`).join('')}
              </select>
            </div>
            <div class="filter-control">
              <label>Colaborador</label>
              <select id="filter-collaborator">
                <option value="">Todos os Colaboradores</option>
                ${this.professionals.map(p => `<option value="${p.id}" ${this.filters.professionalId === p.id ? 'selected' : ''}>${p.name}</option>`).join('')}
              </select>
            </div>
            <div class="filter-control">
              <label>Turno</label>
              <select id="filter-shift">
                <option value="">Todos os Turnos</option>
                <option value="ADMINISTRATIVO" ${this.filters.shift === 'ADMINISTRATIVO' ? 'selected' : ''}>ADMINISTRATIVO</option>
                <option value="TURNO 1" ${this.filters.shift === 'TURNO 1' ? 'selected' : ''}>TURNO 1</option>
                <option value="TURNO 2" ${this.filters.shift === 'TURNO 2' ? 'selected' : ''}>TURNO 2</option>
                <option value="TURNO 3" ${this.filters.shift === 'TURNO 3' ? 'selected' : ''}>TURNO 3</option>
                <option value="NOTURNO" ${this.filters.shift === 'NOTURNO' ? 'selected' : ''}>NOTURNO</option>
              </select>
            </div>
            <div class="filter-control">
              <label>Status</label>
              <select id="filter-status">
                <option value="">Todos os Status</option>
                <option value="DRAFT" ${this.filters.status === 'DRAFT' ? 'selected' : ''}>Rascunho (DRAFT)</option>
                <option value="ISSUED" ${this.filters.status === 'ISSUED' ? 'selected' : ''}>Emitido (ISSUED)</option>
                <option value="CANCELLED" ${this.filters.status === 'CANCELLED' ? 'selected' : ''}>Cancelado</option>
              </select>
            </div>
          </div>
          <div class="filters-actions">
            <button class="btn-rdo btn-rdo-secondary" id="btn-clear-filters" style="font-size: 0.75rem; padding: 4px 10px;">
              <i data-lucide="refresh-cw" style="width: 14px; height: 14px;"></i> Limpar Filtros
            </button>
            <button class="btn-rdo btn-rdo-primary" id="btn-apply-filters" style="font-size: 0.75rem; padding: 4px 14px;">
              <i data-lucide="filter" style="width: 14px; height: 14px;"></i> Aplicar Filtros
            </button>
          </div>
        </div>

        <!-- VIEW TOGGLE & SELECTION ACTION -->
        <div class="view-bar">
          <div class="view-tabs">
            <button class="view-tab ${this.currentView === 'grouped' ? 'active' : ''}" id="tab-view-grouped">
              <i data-lucide="users" style="width: 14px; height: 14px; display: inline-block; vertical-align: middle;"></i> Visão Agrupada por Colaborador (Oficial)
            </button>
            <button class="view-tab ${this.currentView === 'linear' ? 'active' : ''}" id="tab-view-linear">
              <i data-lucide="file-text" style="width: 14px; height: 14px; display: inline-block; vertical-align: middle;"></i> Visão Lista Completa
            </button>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 0.8rem; color: var(--color-graphite-400);">
              ${this.selectedRdoIds.size > 0 ? `<strong>${this.selectedRdoIds.size}</strong> RDO(s) selecionado(s)` : 'Nenhum RDO selecionado'}
            </span>
            ${this.selectedRdoIds.size > 0 ? `
              <button class="btn-rdo btn-rdo-success" id="btn-consolidate-selected" style="font-size: 0.75rem; padding: 4px 10px;">
                <i data-lucide="layers" style="width: 14px; height: 14px;"></i> Consolidar Selecionados (${this.selectedRdoIds.size})
              </button>
            ` : ''}
          </div>
        </div>

        <!-- CONTENT AREA -->
        ${this.isLoading ? `
          <div style="padding: 56px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
            <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            </svg>
            <span class="text-body-small">Carregando apontamentos RDO...</span>
          </div>
        ` : this.rdos.length === 0 ? `
          <div class="empty-state">
            <i data-lucide="file-text" style="width: 36px; height: 36px; margin-bottom: 12px; ;"></i>
            <div class="empty-state-title">Nenhum apontamento encontrado</div>
            <div class="empty-state-desc">Nenhum RDO registrado para os filtros selecionados. Clique no botão abaixo para registrar o primeiro apontamento.</div>
            <button class="btn-rdo btn-rdo-primary" id="btn-empty-create" style="margin-top: 16px;">
              <i data-lucide="plus" style="width: 16px; height: 16px;"></i> Criar Apontamento RDO
            </button>
          </div>
        ` : this.currentView === 'grouped' ? this.renderGroupedView() : this.renderLinearView()}

      </div>

      <!-- NEW / EDIT RDO MODAL -->
      <div class="rdo-modal" id="modal-rdo-form">
        <div class="rdo-modal-box">
          <div class="rdo-modal-header">
            <h2 id="modal-form-title">${this.editingRdoId ? 'Editar Apontamento RDO' : 'Novo Apontamento RDO'}</h2>
            <button id="btn-close-form-modal" style="background: none; border: none; color: var(--color-graphite-400); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          <form id="form-rdo" class="rdo-modal-body">
            <div id="rdo-form-error-banner" style="display: none; background: var(--ia-danger-bg, #450a0a); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 10px 14px; border-radius: 6px; font-size: 0.85rem; margin-bottom: 14px;"></div>
            <!-- Vínculo gravado que não foi encontrado nas listas: advertência
                 âmbar, separada do erro de validação — é diagnóstico de
                 cadastro, não erro de preenchimento. -->
            <div id="rdo-form-hydration-notice" style="display: none; background: rgba(178,148,85,0.10); border: 1px solid rgba(178,148,85,0.45); color: var(--ia-warning-fg, #d9bd84); padding: 10px 14px; border-radius: 6px; font-size: 0.8rem; margin-bottom: 14px;"></div>

            <div class="form-section-title">01 — Contexto operacional</div>
            <div class="form-grid-2">
              <div class="form-group">
                <label for="form-client">Cliente *</label>
                <select id="form-client" required>
                  <option value="">Selecione o Cliente...</option>
                  ${this.clients.map(c => `<option value="${c.id}">${c.name}</option>`).join('')}
                </select>
              </div>
              <div class="form-group">
                <label for="form-project">Projeto *</label>
                <select id="form-project" required disabled><option value="">Selecione primeiro o Cliente...</option></select>
              </div>
              <div class="form-group">
                <label for="form-work-order">OS (Ordem de Serviço) *</label>
                <select id="form-work-order" required disabled><option value="">Selecione primeiro o Projeto...</option></select>
              </div>
              <div class="form-group">
                <label for="form-sub-work-order">Sub-OS *</label>
                <select id="form-sub-work-order" required disabled><option value="">Selecione a OS e o Colaborador...</option></select>
                <span class="form-hint">Somente Sub-OS autorizadas para o colaborador, Cliente, Projeto e OS.</span>
              </div>
            </div>
            <div class="form-group" style="margin-top: 12px;">
              <label for="form-service-front">Frente de Serviço *</label>
              <select id="form-service-front" required disabled><option value="">Selecione primeiro o Projeto...</option></select>
            </div>
            ${this.renderSubOsBreadcrumb()}
            <div class="auto-context">
              ${this.renderAutoContextItem('OS', this.subOsContext?.workOrderCode)}
              ${this.renderAutoContextItem('Projeto', this.subOsContext?.projectLabel)}
              ${this.renderAutoContextItem('Cliente', this.subOsContext?.clientName)}
              ${this.renderAutoContextItem('Unidade / Área', this.subOsContext?.unitAreaLabel)}
            </div>
            <div id="rdo-unit-area-slot"></div>

            <div class="form-section-title">02 — Responsável</div>
            <div class="form-grid-2">
              <div class="form-group">
                <label for="form-professional">Colaborador *</label>
                <select id="form-professional" required>
                  <option value="">${this.professionals.length === 0 ? 'Nenhum profissional ativo cadastrado' : 'Selecione o Colaborador'}</option>
                  ${this.professionals.map(p => `<option value="${p.id}" data-function="${p.functionTitle}">${p.functionTitle ? `${p.name} — ${p.functionTitle}` : p.name}</option>`).join('')}
                </select>
              </div>
              <div class="form-group">
                <label for="form-function-name">Função / Cargo * (conforme cadastro)</label>
                <input type="text" id="form-function-name" readonly required value="" placeholder="Selecione o colaborador..." />
              </div>
            </div>
            <div class="form-section-title">03 — Apontamento</div>
            <div class="form-group" style="margin-bottom: 10px;">
              <label for="form-inspector-comment">Comentário do Fiscal</label>
              <input type="text" id="form-inspector-comment" placeholder="Quando houver" />
            </div>
            <div class="form-grid-2">
              <div class="form-group">
                <label>Data do Apontamento *</label>
                <input type="date" id="form-date" required value="${new Date().toISOString().substring(0, 10)}" />
              </div>
              <div class="form-group">
                <label>Turno *</label>
                <select id="form-shift" required>
                  <option value="ADMINISTRATIVO" selected>ADMINISTRATIVO</option>
                  <option value="TURNO 1">TURNO 1</option>
                  <option value="TURNO 2">TURNO 2</option>
                  <option value="TURNO 3">TURNO 3</option>
                  <option value="NOTURNO">NOTURNO</option>
                </select>
              </div>
            </div>
            <div class="form-grid-4">
              <div class="form-group">
                <label>Hora Inicial *</label>
                <input type="time" id="form-start-time" required value="07:30" />
              </div>
              <div class="form-group">
                <label>Hora Final *</label>
                <input type="time" id="form-end-time" required value="17:18" />
              </div>
              <div class="form-group">
                <label>Horas Normais *</label>
                <input type="number" step="0.1" id="form-normal-hours" required value="8.8" />
              </div>
              <div class="form-group">
                <label>Horas Extras</label>
                <input type="number" step="0.1" id="form-overtime-hours" required value="0" />
              </div>
            </div>

            <div class="form-group">
              <label>OM (Ordem de Manutenção / Solicitação)</label>
              <input type="text" id="form-om" placeholder="Ex: OM-9821 / OS-MANUT-04" />
            </div>

            <div class="form-group">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <label>Descrição Detalhada dos Serviços Executados *</label>
                <button type="button" id="btn-ai-language-review" style="background: none; border: none; color: var(--color-text-muted); font-size: 0.75rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px;">
                  <i data-lucide="sparkles" style="width: 14px; height: 14px;"></i> Revisar português (INSPECT IA REVIEW)
                </button>
              </div>
              <textarea id="form-description" rows="4" required placeholder="Descreva as atividades executadas, equipamentos inspecionados, frentes de trabalho e condições operacionais...">Execução de inspeção visual externa em equipamentos estáticos e linhas de tubulação conforme procedimento técnico PR-INSP-001.</textarea>
            </div>
          </form>
          <div class="rdo-modal-footer">
            <button type="button" class="btn-rdo btn-rdo-secondary" id="btn-cancel-form">Cancelar</button>
            <button type="submit" form="form-rdo" class="btn-rdo btn-rdo-primary" id="btn-save-rdo">
              <i data-lucide="check-circle" style="width: 16px; height: 16px;"></i> <span id="btn-save-rdo-label">Salvar Apontamento</span>
            </button>
          </div>
        </div>
      </div>

      <!-- RDO CONSOLIDADO: 1 PROJETO + 1 FRENTE + 1 PERÍODO + ESCOPO -->
      <div class="rdo-modal" id="modal-consolidado">
        <div class="rdo-modal-box cons-box">
          <div class="rdo-modal-header">
            <div style="display: flex; align-items: center; gap: 10px;">
              <i data-lucide="layers" style="color: var(--color-text-muted); width: 20px; height: 20px;"></i>
              <div>
                <h2>Gerar RDO Consolidado</h2>
                <p class="cons-subtitle">Selecione o Projeto, a Frente de Serviço, o período e os colaboradores.</p>
              </div>
            </div>
            <button id="btn-close-consolidado" class="cons-close">&times;</button>
          </div>

          <div class="cons-body">
            <!-- 01 — CONTEXTO -->
            <div class="cons-section">
              <div class="cons-section-title">01 — Contexto</div>
              <div class="cons-grid-2">
                <div class="cons-field">
                  <label>Projeto *</label>
                  <select id="cons-project">
                    <option value="">${this.projects.length === 0 ? 'Nenhum Projeto cadastrado' : 'Selecione o Projeto'}</option>
                    ${projectOptionsForScope(this.projects, undefined)
                      .map(p => `<option value="${p.id}" ${this.consolidation.projectId === p.id ? 'selected' : ''}>${formatProjectLabel(p)}</option>`).join('')}
                  </select>
                </div>
                <div class="cons-field">
                  <label>Frente de Serviço *</label>
                  ${(() => {
                    const available = selectableFrontsForProject(
                      this.serviceFronts.map(f => ({ ...f, projectId: f.projectId ?? null, status: f.status ?? 'ACTIVE' })),
                      this.consolidation.projectId
                    );
                    const noFronts = Boolean(this.consolidation.projectId) && available.length === 0;
                    return `
                      <select id="cons-front" ${this.consolidation.projectId && !noFronts ? '' : 'disabled'}>
                        <option value="">${!this.consolidation.projectId
                          ? 'Selecione o Projeto primeiro'
                          : noFronts ? 'Nenhuma Frente cadastrada para este Projeto' : 'Selecione a Frente'}</option>
                        ${available.map(f => `<option value="${f.id}" ${this.consolidation.serviceFrontId === f.id ? 'selected' : ''}>${f.code ? `${f.code} — ` : ''}${f.name}</option>`).join('')}
                      </select>
                      ${noFronts ? `
                        <span class="cons-hint">
                          Nenhuma Frente de Serviço cadastrada para este Projeto.
                          <button type="button" id="btn-go-fronts" class="cons-link">Cadastrar Frente de Serviço</button>
                        </span>` : ''}`;
                  })()}
                </div>
              </div>
            </div>

            <!-- 02 — PERÍODO -->
            <div class="cons-section">
              <div class="cons-section-title">02 — Período</div>
              <div class="cons-grid-2">
                <div class="cons-field">
                  <label>Data Inicial *</label>
                  <input type="date" id="cons-start" value="${this.consolidation.startDate}" />
                </div>
                <div class="cons-field">
                  <label>Data Final *</label>
                  <input type="date" id="cons-end" value="${this.consolidation.endDate}" />
                </div>
              </div>
            </div>

            <!-- 03 — COLABORADORES -->
            <div class="cons-section">
              <div class="cons-section-title">03 — Colaboradores</div>
              <div class="cons-radio-group">
                <label class="cons-radio">
                  <input type="radio" name="cons-scope" value="ALL" ${this.consolidation.collaboratorScope === 'ALL' ? 'checked' : ''} />
                  <span>Todos os colaboradores</span>
                </label>
                <label class="cons-radio">
                  <input type="radio" name="cons-scope" value="ONE" ${this.consolidation.collaboratorScope === 'ONE' ? 'checked' : ''} />
                  <span>Um colaborador</span>
                </label>
              </div>
              ${this.consolidation.collaboratorScope === 'ONE' ? `
                <div class="cons-field" style="margin-top: 12px;">
                  <label>Colaborador *</label>
                  <select id="cons-professional" ${this.consolidationScope.collaborators.length === 0 ? 'disabled' : ''}>
                    <option value="">${this.consolidationScope.collaborators.length === 0
                      ? 'Nenhum colaborador com apontamento nesta Frente e período'
                      : 'Selecione o colaborador'}</option>
                    ${this.consolidationScope.collaborators.map(c => `
                      <option value="${c.professionalId}" ${this.consolidation.professionalId === c.professionalId ? 'selected' : ''}>
                        ${c.name}${c.functionName ? ` — ${c.functionName}` : ''} (${c.entryCount})
                      </option>`).join('')}
                  </select>
                  <span class="cons-hint">Somente colaboradores que possuem apontamentos nos filtros escolhidos.</span>
                </div>` : ''}
            </div>

            <!-- RESUMO DA SELEÇÃO -->
            <div class="cons-summary">
              <div class="cons-section-title">Resumo da seleção</div>
              <div class="cons-summary-grid">
                ${this.renderConsolidationSummaryItem('Projeto', this.consolidationSummary().project)}
                ${this.renderConsolidationSummaryItem('Frente', this.consolidationSummary().front)}
                ${this.renderConsolidationSummaryItem('Período', this.consolidationSummary().period)}
                ${this.renderConsolidationSummaryItem('Colaboradores', this.consolidationSummary().collaborators)}
                ${this.renderConsolidationSummaryItem('Apontamentos encontrados', this.consolidationSummary().entryCount)}
                ${this.renderConsolidationSummaryItem('Colaboradores com registro', this.consolidationSummary().collaboratorCount)}
              </div>
            </div>

            ${this.consolidationError ? `<div class="cons-error">${this.consolidationError}</div>` : ''}
            ${this.consolidationBlockReason() ? `<div class="cons-blocked">${this.consolidationBlockReason()}</div>` : ''}

            ${this.consolidationModel ? this.renderConsolidationPreview(this.consolidationModel) : ''}
          </div>

          <div class="rdo-modal-footer">
            <button type="button" class="btn-rdo btn-rdo-secondary" id="btn-close-consolidado-footer">Cancelar</button>
            <button type="button" class="btn-rdo btn-rdo-secondary" id="btn-preview-consolidado" ${this.consolidationBlockReason() ? 'disabled' : ''}>
              <i data-lucide="eye" style="width: 15px; height: 15px;"></i> Visualizar RDO
            </button>
            <button type="button" class="btn-rdo btn-rdo-primary" id="btn-generate-consolidado" ${this.consolidationBlockReason() ? 'disabled' : ''}>
              <i data-lucide="file-text" style="width: 15px; height: 15px;"></i> Gerar RDO Consolidado
            </button>
          </div>
        </div>
      </div>

      <!-- FULLSCREEN PDF VIEWER MODAL -->
      <div class="rdo-modal" id="modal-pdf-viewer">
        <div class="pdf-modal-box">
          <div class="rdo-modal-header">
            <div style="display: flex; align-items: center; gap: 10px;">
              <i data-lucide="file-text" style="color: var(--color-text-muted); width: 20px; height: 20px;"></i>
              <h2 id="pdf-viewer-title">Visualização Oficial de RDO</h2>
            </div>
            <button id="btn-close-pdf-modal" style="background: var(--status-danger-solid); border: none; color: var(--color-text-primary); padding: 6px 14px; border-radius: 4px; font-weight: 600; font-size: 0.85rem; cursor: pointer;">
              ✕ Fechar Janela (ESC)
            </button>
          </div>
          <app-pdf-preview id="rdo-pdf-preview" style="flex: 1;"></app-pdf-preview>
        </div>
      </div>

    `;

    createIcons({
      icons: { Plus, FileText, Download, Sparkles, Filter, RefreshCw, Trash2, Edit, CheckCircle, Eye, Printer, Users, Calendar, Clock, Layers },
      nameAttr: "data-lucide",
      root: this
    });

    this.attachEvents();
  }

  private renderGroupedView(): string {
    return this.collaboratorGroups.map((group, gIdx) => {
      const seqStr = String(gIdx + 1).padStart(2, '0');
      return `
        <div class="collab-card">
          <div class="collab-card-header">
            <div class="collab-name-badge">
              <input type="checkbox" class="chk-group-collab" data-prof-id="${group.professionalId}" title="Selecionar todos deste colaborador" />
              <strong>${seqStr} • ${group.professionalName.toUpperCase()}</strong>
              <span>${group.functionName}</span>
            </div>
            <div class="collab-stats-badges">
              <span class="stat-badge">Diárias: <strong>${group.totalDays}</strong></span>
              <span class="stat-badge">H. Normais: <strong>${group.totalNormalHours.toFixed(1)}h</strong></span>
              <span class="stat-badge">H. Extras: <strong>${group.totalOvertimeHours.toFixed(1)}h</strong></span>
            </div>
          </div>
          <table class="rdo-table">
            <thead>
              <tr>
                <th style="width: 38px; text-align: center;">Sel</th>
                <th>Data</th>
                <th>Horário</th>
                <th>H. Ext</th>
                <th>OM</th>
                <th>Turno</th>
                <th>OS</th>
                <th>Descrição</th>
                <th style="text-align: right; width: 140px;">Ações</th>
              </tr>
            </thead>
            <tbody>
              ${group.rdos.map(rdo => `
                <tr>
                  <td style="text-align: center;">
                    <input type="checkbox" class="chk-rdo-item" data-id="${rdo.id}" ${this.selectedRdoIds.has(rdo.id) ? 'checked' : ''} />
                  </td>
                  <td><strong>${rdo.date.split('-').reverse().join('/')}</strong></td>
                  <td>${rdo.startTime} às ${rdo.endTime}</td>
                  <td style="color: ${rdo.overtimeHours > 0 ? 'var(--status-warning-solid)' : 'var(--color-graphite-400)'}; font-weight: ${rdo.overtimeHours > 0 ? '700' : 'normal'}">
                    ${rdo.overtimeHours.toFixed(2)}h
                  </td>
                  <td>${rdo.om || '-'}</td>
                  <td><span style="font-size: 0.75rem; color: var(--color-graphite-400);">${rdo.shift}</span></td>
                  <td><span style="font-size: 0.75rem; color: var(--color-text-muted); font-weight: 600;">${rdo.workOrderCode || '-'}</span></td>
                  <td style="max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${rdo.description}">
                    ${rdo.description || '-'}
                  </td>
                  <td style="text-align: right;">
                    <div style="display: inline-flex; gap: 4px;">
                      <button class="btn-rdo btn-rdo-secondary btn-print-individual" data-id="${rdo.id}" style="padding: 4px 8px; font-size: 0.75rem;" title="Visualizar / Imprimir RDO Individual">
                        <i data-lucide="printer" style="width: 13px; height: 13px; color: var(--color-text-muted);"></i>
                      </button>
                      ${this.canEditEntries() ? `
                        <button class="btn-rdo btn-rdo-secondary btn-edit-rdo" data-id="${rdo.id}" style="padding: 4px 8px; font-size: 0.75rem;" title="Editar Apontamento">
                          <i data-lucide="edit" style="width: 13px; height: 13px; color: var(--color-graphite-300);"></i>
                        </button>` : ''}
                      <button class="btn-rdo btn-rdo-secondary btn-delete-rdo" data-id="${rdo.id}" style="padding: 4px 8px; font-size: 0.75rem;" title="Excluir Apontamento">
                        <i data-lucide="trash-2" style="width: 13px; height: 13px; color: var(--status-danger-solid);"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    }).join('');
  }

  private renderLinearView(): string {
    return `
      <div style="background: var(--color-graphite-800); border: 1px solid var(--color-primary); border-radius: 8px; overflow: hidden;">
        <table class="rdo-table">
          <thead>
            <tr>
              <th style="width: 38px; text-align: center;">Sel</th>
              <th>Data</th>
              <th>Colaborador</th>
              <th>Função</th>
              <th>Horário</th>
              <th>H. Ext</th>
              <th>OM</th>
              <th>OS / Sub-OS</th>
              <th>Projeto</th>
              <th>Frente</th>
              <th style="text-align: right; width: 140px;">Ações</th>
            </tr>
          </thead>
          <tbody>
            ${this.rdos.map(rdo => `
              <tr>
                <td style="text-align: center;">
                  <input type="checkbox" class="chk-rdo-item" data-id="${rdo.id}" ${this.selectedRdoIds.has(rdo.id) ? 'checked' : ''} />
                </td>
                <td><strong>${rdo.date.split('-').reverse().join('/')}</strong></td>
                <td><strong>${rdo.professionalName}</strong></td>
                <td>${rdo.functionName}</td>
                <td>${rdo.startTime} - ${rdo.endTime}</td>
                <td style="color: ${rdo.overtimeHours > 0 ? 'var(--status-warning-solid)' : 'var(--color-graphite-400)'};">
                  ${rdo.overtimeHours.toFixed(2)}h
                </td>
                <td>${rdo.om || '-'}</td>
                <td>${rdo.workOrderCode} ${rdo.subWorkOrderCode ? `/ ${rdo.subWorkOrderCode}` : ''}</td>
                <td>${rdo.projectName || '-'}</td>
                <td>${rdo.serviceFrontName || '-'}</td>
                <td style="text-align: right;">
                  <div style="display: inline-flex; gap: 4px;">
                    <button class="btn-rdo btn-rdo-secondary btn-print-individual" data-id="${rdo.id}" style="padding: 4px 8px; font-size: 0.75rem;" title="Visualizar / Imprimir RDO Individual">
                      <i data-lucide="printer" style="width: 13px; height: 13px; color: var(--color-text-muted);"></i>
                    </button>
                    ${this.canEditEntries() ? `
                      <button class="btn-rdo btn-rdo-secondary btn-edit-rdo" data-id="${rdo.id}" style="padding: 4px 8px; font-size: 0.75rem;" title="Editar">
                        <i data-lucide="edit" style="width: 13px; height: 13px;"></i>
                      </button>` : ''}
                    ${(rdo.changeHistory?.length ?? 0) > 0 ? `
                      <button class="btn-rdo btn-rdo-secondary btn-history-rdo" data-id="${rdo.id}" style="padding: 4px 8px; font-size: 0.75rem;" title="Histórico de alterações (${rdo.changeHistory!.length})">
                        <i data-lucide="clock" style="width: 13px; height: 13px;"></i>
                      </button>` : ''}
                    <button class="btn-rdo btn-rdo-secondary btn-delete-rdo" data-id="${rdo.id}" style="padding: 4px 8px; font-size: 0.75rem;" title="Excluir">
                      <i data-lucide="trash-2" style="width: 13px; height: 13px; color: var(--status-danger-solid);"></i>
                    </button>
                  </div>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  private attachEvents() {
    // Tab switchers
    this.querySelector('#tab-view-grouped')?.addEventListener('click', () => {
      this.currentView = 'grouped';
      this.render();
    });
    this.querySelector('#tab-view-linear')?.addEventListener('click', () => {
      this.currentView = 'linear';
      this.render();
    });

    // Filter controls
    const btnApplyFilters = this.querySelector('#btn-apply-filters');
    btnApplyFilters?.addEventListener('click', () => {
      this.filters = {
        clientId: (this.querySelector('#filter-client') as HTMLSelectElement)?.value || '',
        startDate: (this.querySelector('#filter-start-date') as HTMLInputElement)?.value || '',
        endDate: (this.querySelector('#filter-end-date') as HTMLInputElement)?.value || '',
        workOrderId: (this.querySelector('#filter-os') as HTMLSelectElement)?.value || '',
        subWorkOrderId: (this.querySelector('#filter-sub-os') as HTMLSelectElement)?.value || '',
        projectId: (this.querySelector('#filter-project') as HTMLSelectElement)?.value || '',
        serviceFrontId: (this.querySelector('#filter-front') as HTMLSelectElement)?.value || '',
        professionalId: (this.querySelector('#filter-collaborator') as HTMLSelectElement)?.value || '',
        shift: (this.querySelector('#filter-shift') as HTMLSelectElement)?.value || '',
        status: (this.querySelector('#filter-status') as HTMLSelectElement)?.value || ''
      };
      this.initData();
    });

    this.querySelector('#btn-clear-filters')?.addEventListener('click', () => {
      this.filters = {
        clientId: '',
        startDate: '',
        endDate: '',
        workOrderId: '',
        subWorkOrderId: '',
        projectId: '',
        serviceFrontId: '',
        professionalId: '',
        functionName: '',
        shift: '',
        status: ''
      };
      this.initData();
    });

    // Cascading Client filter change
    this.querySelector('#filter-client')?.addEventListener('change', (e) => {
      const cliId = (e.target as HTMLSelectElement).value;
      this.filters = { ...this.filters, clientId: cliId, workOrderId: '', subWorkOrderId: '', projectId: '', serviceFrontId: '' };
      this.render();
    });

    // Projeto → Frente: trocar o Projeto descarta uma Frente de outro Projeto.
    this.querySelector('#filter-project')?.addEventListener('change', (e) => {
      const projectId = (e.target as HTMLSelectElement).value;
      const keptFront = keepFrontSelectionOnProjectChange(
        this.serviceFronts.map(f => ({ id: f.id, projectId: f.projectId ?? null, status: f.status ?? 'ACTIVE' })),
        projectId,
        this.filters.serviceFrontId || ''
      );
      this.filters = { ...this.filters, projectId, serviceFrontId: keptFront };
      this.render();
    });

    // Cascading OS filter change
    this.querySelector('#filter-os')?.addEventListener('change', (e) => {
      const woId = (e.target as HTMLSelectElement).value;
      this.filters = { ...this.filters, workOrderId: woId, subWorkOrderId: '' };
      this.render();
    });

    // Item selection checkboxes
    this.querySelectorAll('.chk-rdo-item').forEach(chk => {
      chk.addEventListener('change', (e) => {
        const id = (e.target as HTMLInputElement).getAttribute('data-id');
        if (!id) return;
        if ((e.target as HTMLInputElement).checked) {
          this.selectedRdoIds.add(id);
        } else {
          this.selectedRdoIds.delete(id);
        }
        this.render();
      });
    });

    // Group select checkboxes
    this.querySelectorAll('.chk-group-collab').forEach(chk => {
      chk.addEventListener('change', (e) => {
        const profId = (e.target as HTMLInputElement).getAttribute('data-prof-id');
        const isChecked = (e.target as HTMLInputElement).checked;
        const group = this.collaboratorGroups.find(g => g.professionalId === profId);
        if (group) {
          group.rdos.forEach(r => {
            if (isChecked) this.selectedRdoIds.add(r.id);
            else this.selectedRdoIds.delete(r.id);
          });
        }
        this.render();
      });
    });

    // Cascata única: Cliente → Projeto → OS → Sub-OS; Frente depende do Projeto.
    const updateModalOsAndFrontOptions = async (selectedWoId: string | null = null, selectedFrontId: string | null = null) => {
      await this.refreshModalWorkOrderOptions(selectedWoId);
      this.refreshFrontOptions(selectedFrontId);
      this.refreshSubOsSelect();
    };

    this.querySelector('#form-client')?.addEventListener('change', async () => {
      const clientId = (this.querySelector('#form-client') as HTMLSelectElement).value;
      ++this.subOsRequest;
      ++this.modalWorkOrderRequest;
      this.subOsOptions = [];
      this.subOsContext = null;
      this.refreshModalProjectOptions();
      this.refreshSubOsSelect();
      this.refreshAutoContext();
      this.refreshModalUnitArea();
      this.refreshFrontOptions(null);
      await this.loadModalClientCatalogues(clientId);
      if ((this.querySelector('#form-client') as HTMLSelectElement).value !== clientId) return;
      this.refreshModalProjectOptions();
      await updateModalOsAndFrontOptions();
    });

    this.querySelector('#form-project')?.addEventListener('change', async () => {
      ++this.modalWorkOrderRequest;
      this.subOsContext = null;
      this.refreshSubOsSelect();
      this.refreshAutoContext();
      this.refreshModalUnitArea();
      this.refreshFrontOptions(null);
      await updateModalOsAndFrontOptions();
    });

    // Create Modal triggers
    const openCreateModal = async () => {
      this.editingRdoId = null;
      // Apontamento novo não tem vínculo gravado a repor.
      this.editHydration = null;
      this.renderEditHydrationNotice();
      // Formulário novo começa sem colaborador, portanto sem Sub-OS.
      this.subOsOptions = [];
      this.subOsContext = null;
      ++this.subOsRequest;
      this.refreshSubOsSelect(null);
      this.refreshAutoContext();
      this.refreshModalUnitArea();
      const modal = this.querySelector('#modal-rdo-form') as HTMLElement;
      const form = this.querySelector('#form-rdo') as HTMLFormElement;
      form?.reset();
      const errBanner = this.querySelector('#rdo-form-error-banner') as HTMLElement;
      if (errBanner) errBanner.style.display = 'none';

      (this.querySelector('#modal-form-title') as HTMLElement).innerText = 'Novo Apontamento RDO';
      const saveLabel = this.querySelector('#btn-save-rdo-label') as HTMLElement | null;
      if (saveLabel) saveLabel.innerText = 'Salvar Apontamento';
      (this.querySelector('#form-date') as HTMLInputElement).value = new Date().toISOString().substring(0, 10);
      (this.querySelector('#form-start-time') as HTMLInputElement).value = '07:30';
      (this.querySelector('#form-end-time') as HTMLInputElement).value = '17:18';
      (this.querySelector('#form-normal-hours') as HTMLInputElement).value = '8.8';
      (this.querySelector('#form-overtime-hours') as HTMLInputElement).value = '0';
      (this.querySelector('#form-function-name') as HTMLInputElement).value = '';
      (this.querySelector('#form-description') as HTMLTextAreaElement).value = '';
      
      const formClient = this.querySelector('#form-client') as HTMLSelectElement;
      formClient.disabled = false;
      formClient.value = this.filters.clientId || '';
      await this.loadModalClientCatalogues(formClient.value);
      this.refreshModalProjectOptions();
      await updateModalOsAndFrontOptions();
      modal?.classList.add('active');
    };

    this.querySelector('#btn-open-create')?.addEventListener('click', openCreateModal);
    this.querySelector('#btn-empty-create')?.addEventListener('click', openCreateModal);

    // Close Modals
    const closeFormModal = () => {
      this.querySelector('#modal-rdo-form')?.classList.remove('active');
    };
    this.querySelector('#btn-close-form-modal')?.addEventListener('click', closeFormModal);
    this.querySelector('#btn-cancel-form')?.addEventListener('click', closeFormModal);

    const closePdfModal = () => {
      this.querySelector('#modal-pdf-viewer')?.classList.remove('active');
    };
    this.querySelector('#btn-close-pdf-modal')?.addEventListener('click', closePdfModal);

    // Work Order change -> update Sub-OS options
    this.querySelector('#form-work-order')?.addEventListener('change', () => {
      this.subOsContext = null;
      this.refreshSubOsSelect();
      this.refreshAutoContext();
      this.refreshModalUnitArea();
    });

    // COLABORADOR → Sub-OS. Trocar o colaborador recarrega as Sub-OS dele e
    // descarta a Sub-OS do anterior.
    this.querySelector('#form-professional')?.addEventListener('change', async (e) => {
      const sel = e.target as HTMLSelectElement;
      const opt = sel.options[sel.selectedIndex];
      const fnTitle = opt?.getAttribute('data-function');
      const fnInput = this.querySelector('#form-function-name') as HTMLInputElement;
      if (fnInput) {
        // Função vem do cadastro do Professional. Sem função cadastrada o campo
        // fica vazio — não se inventa "INSPETOR" nem se usa o papel de acesso.
        fnInput.value = fnTitle || '';
      }

      await this.reloadSubOsForProfessional(null);
      this.refreshSubOsSelect();
      this.applySubOsContextToForm();
      this.refreshAutoContext();
    });

    // SUB-OS → OS, Projeto, Cliente e Unidade/Área, todos automáticos.
    this.querySelector('#form-sub-work-order')?.addEventListener('change', (e) => {
      const subOsId = (e.target as HTMLSelectElement).value;
      const selectedClient = (this.querySelector('#form-client') as HTMLSelectElement | null)?.value || '';
      const projectId = (this.querySelector('#form-project') as HTMLSelectElement | null)?.value || '';
      const workOrderId = (this.querySelector('#form-work-order') as HTMLSelectElement | null)?.value || '';
      this.subOsContext = this.subOsOptions.find(o => o.subOs.id === subOsId &&
        o.context.clientId === selectedClient && o.context.projectId === projectId &&
        o.context.workOrderId === workOrderId)?.context ?? null;
      this.refreshAutoContext();
      this.applySubOsContextToForm();
    });

    // Form submission (Create / Update)
    this.querySelector('#form-rdo')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const errBanner = this.querySelector('#rdo-form-error-banner') as HTMLElement;
      if (errBanner) errBanner.style.display = 'none';

      const selectedClientId = (this.querySelector('#form-client') as HTMLSelectElement)?.value;
      if (!selectedClientId) {
        if (errBanner) {
          errBanner.textContent = 'Erro: Seleção de Cliente é obrigatória para registrar apontamento de RDO.';
          errBanner.style.display = 'block';
        }
        return;
      }

      const ctx = {
        ...this.getContext(),
        activeClientId: selectedClientId as ClientId
      };
      if (!platform.rdoService) return;

      const subWoVal = (this.querySelector('#form-sub-work-order') as HTMLSelectElement).value;
      const frontVal = (this.querySelector('#form-service-front') as HTMLSelectElement | null)?.value || '';
      const projectVal = (this.querySelector('#form-project') as HTMLSelectElement | null)?.value || '';
      const workOrderVal = (this.querySelector('#form-work-order') as HTMLSelectElement | null)?.value || '';
      const professionalVal = (this.querySelector('#form-professional') as HTMLSelectElement | null)?.value || '';
      if (!projectVal || !this.modalProjects.some(p => p.clientId === selectedClientId && p.id === projectVal)) {
        this.showRdoFormError('Selecione um Projeto válido e vinculado ao Cliente.');
        return;
      }
      const workOrderSelect = this.querySelector('#form-work-order') as HTMLSelectElement | null;
      if (!workOrderVal || !workOrderSelect?.selectedOptions[0]?.value ||
          !this.workOrders.some(o => o.id === workOrderVal && o.clientId === selectedClientId)) {
        this.showRdoFormError('Selecione uma OS válida vinculada ao Projeto.');
        return;
      }
      // Unidade só é escolhida à mão quando a Sub-OS não a determina.
      const unitAreaVal = (this.querySelector('#form-unit-area') as HTMLSelectElement | null)?.value
        || this.subOsContext?.unitAreaId || '';

      if (!subWoVal) {
        if (errBanner) {
          errBanner.textContent = 'Selecione a Sub-OS do apontamento.';
          errBanner.style.display = 'block';
        }
        return;
      }

      /**
       * Vínculo gravado que não foi achado na lista NÃO pode ser salvo por
       * cima. O select ficou em branco sozinho; gravar agora escreveria
       * `null` sobre um dado que existia, sem ninguém ter decidido isso.
       */
      // Seleção de Cliente deve coincidir com Sub-OS efetivamente autorizada.
      if (!this.subOsContext || this.subOsContext.clientId !== selectedClientId ||
          this.subOsContext.projectId !== projectVal || this.subOsContext.workOrderId !== workOrderVal ||
          !this.subOsOptions.some(o => o.subOs.id === subWoVal &&
            o.context.clientId === selectedClientId && o.context.projectId === projectVal &&
            o.context.workOrderId === workOrderVal) ||
          !professionalVal) {
        if (errBanner) {
          errBanner.textContent = 'Sub-OS não autorizada para este Cliente e colaborador.';
          errBanner.style.display = 'block';
        }
        return;
      }

      const notFound = missingRestoredLevels(this.editHydration);
      if (notFound.length > 0 && errBanner) {
        errBanner.textContent =
          `Não foi possível confirmar ${notFound.map(l => l.label).join(", ")} deste apontamento: `
          + `o cadastro foi removido ou está fora do Cliente carregado. `
          + `Escolha novamente antes de salvar — gravar agora apagaria o vínculo.`;
        errBanner.style.display = 'block';
        return;
      }

      // Frente de Serviço é obrigatória: sem ela o apontamento não tem
      // documento consolidado a que pertencer.
      if (!frontVal || !this.modalServiceFronts.some(f => f.id === frontVal &&
        f.clientId === selectedClientId && f.projectId === projectVal &&
        (f.status ?? 'ACTIVE') === 'ACTIVE')) {
        if (errBanner) {
          errBanner.textContent = 'Selecione a Frente de Serviço do apontamento.';
          errBanner.style.display = 'block';
        }
        return;
      }

      const req = {
        clientId: selectedClientId,
        workOrderId: workOrderVal,
        subWorkOrderId: subWoVal || null,
        serviceFrontId: frontVal,
        projectId: projectVal || null,
        unitAreaId: unitAreaVal || null,
        inspectorComment: (this.querySelector('#form-inspector-comment') as HTMLInputElement | null)?.value || null,
        professionalId: professionalVal,
        functionName: (this.querySelector('#form-function-name') as HTMLInputElement).value,
        date: (this.querySelector('#form-date') as HTMLInputElement).value,
        shift: (this.querySelector('#form-shift') as HTMLSelectElement).value,
        startTime: (this.querySelector('#form-start-time') as HTMLInputElement).value,
        endTime: (this.querySelector('#form-end-time') as HTMLInputElement).value,
        normalHours: Number((this.querySelector('#form-normal-hours') as HTMLInputElement).value),
        overtimeHours: Number((this.querySelector('#form-overtime-hours') as HTMLInputElement).value),
        om: (this.querySelector('#form-om') as HTMLInputElement).value || null,
        description: (this.querySelector('#form-description') as HTMLTextAreaElement).value
      };

      try {
        if (this.editingRdoId) {
          // Alterar colaborador, data, projeto, frente ou OS muda de quem é o
          // apontamento e a que documento ele pertence: confirma antes.
          const existing = await platform.rdoService.getRdo(ctx, this.editingRdoId as RdoId);
          if (existing) {
            const changes = diffRdoEntry(existing as any, req as any);
            if (requiresConfirmation(changes) && !confirm('Deseja salvar as alterações deste apontamento RDO?')) {
              return;
            }
          }
          await platform.rdoService.updateRdo(ctx, this.editingRdoId as RdoId, req);
        } else {
          await platform.rdoService.createRdo(ctx, req);
        }
        closeFormModal();
        // A lista volta atualizada do repositório, sem exigir F5.
        await this.initData();
      } catch (err: any) {
        console.error('[RDO] Falha ao salvar o apontamento', err);
        let message = err?.safeMessage || err?.message || String(err);
        if (
          err?.code === 'CONFLICT' ||
          err?.status === 409 ||
          (typeof message === 'string' && (
            message.includes('CONFLICT') ||
            message.includes('conflito') ||
            message.includes('duplicate') ||
            message.includes('already exists') ||
            message.includes('Já existe')
          ))
        ) {
          message = "Já existe um apontamento registrado para este colaborador nesta data e turno.";
        }
        if (errBanner) {
          errBanner.textContent = "Erro ao salvar RDO: " + message;
          errBanner.style.display = 'block';
        } else {
          alert("Erro ao salvar RDO:\n" + message);
        }
      }
    });

    // Edit button handlers
    this.querySelectorAll('.btn-edit-rdo').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (!id || !platform.rdoService) return;
        const errBanner = this.querySelector('#rdo-form-error-banner') as HTMLElement;
        if (errBanner) errBanner.style.display = 'none';

        const ctx = this.getContext();
        const rdo = await platform.rdoService.getRdo(ctx, id as RdoId);
        if (!rdo) return;

        // Apontamento congelado por consolidado aprovado/assinado não abre
        // para edição silenciosa — a tela diz por quê.
        const editability = await platform.rdoService.getEntryEditability(ctx, id as RdoId);
        if (!editability.editable) {
          alert(editability.reason);
          return;
        }

        this.editingRdoId = id;
        (this.querySelector('#modal-form-title') as HTMLElement).innerText = 'Editar Apontamento RDO';
        const saveLabel = this.querySelector('#btn-save-rdo-label') as HTMLElement | null;
        if (saveLabel) saveLabel.innerText = 'Salvar Alterações';
        
        // Restaurar os níveis NA ORDEM: Cliente → Projeto → OS → Sub-OS → Frente.
        const rdoClient = rdo.clientId || this.workOrders.find(w => w.id === rdo.workOrderId)?.clientId || '';
        const clientSelect = this.querySelector('#form-client') as HTMLSelectElement;
        clientSelect.value = rdoClient;
        clientSelect.disabled = false;
        await this.loadModalClientCatalogues(rdoClient);
        const rdoProjectId = rdo.projectId
          || this.modalServiceFronts.find(f => f.id === rdo.serviceFrontId)?.projectId || '';
        this.refreshModalProjectOptions(rdoProjectId);
        await updateModalOsAndFrontOptions(rdo.workOrderId, rdo.serviceFrontId || null);
        (this.querySelector('#form-professional') as HTMLSelectElement).value = rdo.professionalId;
        await this.reloadSubOsForProfessional(rdo.subWorkOrderId ?? null);
        this.refreshSubOsSelect(rdo.subWorkOrderId ?? null);
        this.subOsContext = this.subOsOptions.find(o => o.subOs.id === rdo.subWorkOrderId
          && o.context.clientId === rdoClient && o.context.projectId === rdoProjectId
          && o.context.workOrderId === rdo.workOrderId)?.context ?? null;
        this.refreshAutoContext();
        this.refreshModalUnitArea(rdo.unitAreaId ?? null);
        this.refreshFrontOptions(rdo.serviceFrontId || null);
        // .value, não setAttribute: o atributo não atualiza o valor corrente
        // de um input já renderizado, e o campo abria em branco.
        const inspectorComment = this.querySelector('#form-inspector-comment') as HTMLInputElement | null;
        if (inspectorComment) inspectorComment.value = rdo.inspectorComment || '';
        const profObj = this.professionals.find(p => p.id === rdo.professionalId);
        // Na edição, a função vem do cadastro atual do Professional; a do
        // apontamento é a queda para um profissional que já não existe.
        (this.querySelector('#form-function-name') as HTMLInputElement).value = profObj?.functionTitle || rdo.functionName || '';
        (this.querySelector('#form-date') as HTMLInputElement).value = rdo.date;
        (this.querySelector('#form-shift') as HTMLSelectElement).value = rdo.shift;
        (this.querySelector('#form-start-time') as HTMLInputElement).value = rdo.startTime;
        (this.querySelector('#form-end-time') as HTMLInputElement).value = rdo.endTime;
        (this.querySelector('#form-normal-hours') as HTMLInputElement).value = String(rdo.normalHours);
        (this.querySelector('#form-overtime-hours') as HTMLInputElement).value = String(rdo.overtimeHours);
        (this.querySelector('#form-om') as HTMLInputElement).value = rdo.om || '';
        (this.querySelector('#form-description') as HTMLTextAreaElement).value = rdo.description;

        this.refreshSubOsSelect(rdo.subWorkOrderId ?? null);

        /**
         * CONFERE o que foi reposto. `select.value = X` sem `<option X>`
         * deixa o campo em `""` EM SILÊNCIO — e o salvamento grava esse
         * vazio, apagando o vínculo do apontamento. Acontece de verdade
         * quando o Projeto/OS/Frente foi removido, ou quando o apontamento
         * é de um Cliente diferente do carregado nas listas.
         *
         * O aviso diz qual vínculo não foi achado, e o salvamento é
         * bloqueado até a pessoa resolver: escrever `null` por cima de um
         * dado que existia seria perda silenciosa.
         */
        this.editHydration = verifyRestoredContext([
          { label: "o Cliente", persistedId: rdoClient, options: this.clients },
          { label: "a Ordem de Serviço", persistedId: rdo.workOrderId, options: this.workOrders },
          { label: "a Sub-OS", persistedId: rdo.subWorkOrderId, options: this.subOsOptions.map(o => ({ id: String(o.subOs.id) })) },
          { label: "a Frente de Serviço", persistedId: rdo.serviceFrontId, options: this.serviceFronts },
          { label: "o Projeto", persistedId: rdoProjectId, options: this.projects },
          { label: "o Colaborador", persistedId: rdo.professionalId, options: this.professionals }
        ]);
        this.renderEditHydrationNotice();

        this.querySelector('#modal-rdo-form')?.classList.add('active');
      });
    });

    // HISTÓRICO — o que mudou em cada edição, de quê para quê.
    this.querySelectorAll('.btn-history-rdo').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (!id) return;
        try {
          const trail = await platform.rdoService.getEntryTrail(this.getContext(), id as RdoId);
          if (!trail) return;

          const br = (iso: string) => {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return iso;
            const pad = (n: number) => String(n).padStart(2, '0');
            return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
          };
          const shown = (value: unknown) => (value === null || value === undefined || value === '' ? '—' : String(value));

          const lines: string[] = [
            `HISTÓRICO DO APONTAMENTO`,
            ``,
            `Criado em ${br(trail.createdAt)}${trail.createdBy ? ` por ${trail.createdBy}` : ''}`,
            `Versão atual: ${trail.version}`,
            ``
          ];

          if (trail.changeHistory.length === 0) {
            lines.push('Nenhuma alteração registrada desde a criação.');
          } else {
            for (const record of trail.changeHistory) {
              lines.push(`${br(record.changedAt)}${record.changedByUserId ? ` — ${record.changedByUserId}` : ''}`);
              for (const change of record.changes) {
                lines.push(`  ${formatEntryFieldLabel(change.field)} alterado`);
                lines.push(`    ANTES:  ${shown(change.previousValue)}`);
                lines.push(`    DEPOIS: ${shown(change.newValue)}`);
              }
              lines.push('');
            }
          }

          if (trail.consolidatedIn.length > 0) {
            lines.push(`Consolidados que contêm este apontamento: ${trail.consolidatedIn.map(c => `${c.id} (${c.status ?? 'sem status'})`).join(', ')}`);
          }

          alert(lines.join('\n'));
        } catch (err: any) {
          console.error('[RDO] Falha ao carregar o histórico do apontamento', err);
          alert(err?.safeMessage || err?.message || 'Não foi possível carregar o histórico.');
        }
      });
    });

    // Delete button handlers
    this.querySelectorAll('.btn-delete-rdo').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (!id || !platform.rdoService) return;
        if (confirm("Deseja realmente cancelar/excluir este apontamento RDO?")) {
          try {
            await platform.rdoService.deleteRdo(this.getContext(), id as RdoId, 'Cancelado pelo usuário na interface');
            this.selectedRdoIds.delete(id);
            await this.initData();
          } catch (err: any) {
            alert("Erro ao excluir RDO: " + err.message);
          }
        }
      });
    });

    // Print Individual RDO
    this.querySelectorAll('.btn-print-individual').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (!id || !platform.rdoService) return;
        console.log("[REAL-PDF-FLOW]", {
          module: "RDO",
          stage: "HANDLER_START",
          handler: "printIndividual",
          rdoId: id
        });
        try {
          const snapshot = await platform.rdoService.buildIndividualDocumentSnapshot(this.getContext(), id as RdoId);
          console.log("[REAL-PDF-FLOW]", {
            module: "RDO",
            stage: "SNAPSHOT_RECEIVED",
            rdoId: id,
            createdAt: snapshot.createdAt,
            createdAtType: typeof snapshot.createdAt
          });
          const pdfViewer = this.querySelector('#rdo-pdf-preview') as any;
          (this.querySelector('#pdf-viewer-title') as HTMLElement).innerText = `RDO Individual • ${snapshot.documentNumber || id}`;
          pdfViewer.snapshot = snapshot;
          this.querySelector('#modal-pdf-viewer')?.classList.add('active');
        } catch (err: any) {
          alert("Erro ao gerar visualização do RDO: " + err.message);
        }
      });
    });

    // RDO CONSOLIDADO — Projeto + Frente + período + escopo de colaborador.
    const reopenConsolidationModal = () => {
      this.render();
      this.querySelector('#modal-consolidado')?.classList.add('active');
    };

    this.querySelector('#btn-open-consolidado')?.addEventListener('click', async () => {
      this.consolidationError = null;
      this.consolidationModel = null;
      // A janela sempre abre em TODOS OS COLABORADORES.
      this.consolidation.collaboratorScope = 'ALL';
      this.consolidation.professionalId = '';
      await this.refreshConsolidationScope();
      reopenConsolidationModal();
    });

    ['#btn-close-consolidado', '#btn-close-consolidado-footer'].forEach(sel => {
      this.querySelector(sel)?.addEventListener('click', () => {
        this.querySelector('#modal-consolidado')?.classList.remove('active');
      });
    });

    // Projeto sem Frente: o Projeto continua visível e o usuário é levado ao
    // cadastro em vez de ficar travado.
    this.querySelector('#btn-go-fronts')?.addEventListener('click', () => {
      router.navigate('/management/fronts');
    });

    this.querySelector('#cons-project')?.addEventListener('change', async (e) => {
      this.consolidation.projectId = (e.target as HTMLSelectElement).value;
      // Frente de outro Projeto nunca permanece selecionada.
      this.consolidation.serviceFrontId = keepFrontSelectionOnProjectChange(
        this.serviceFronts.map(f => ({ id: f.id, projectId: f.projectId ?? null, status: f.status ?? 'ACTIVE' })),
        this.consolidation.projectId,
        this.consolidation.serviceFrontId
      );
      this.consolidationModel = null;
      this.consolidationError = null;
      await this.refreshConsolidationScope();
      reopenConsolidationModal();
    });

    ['#cons-front', '#cons-start', '#cons-end'].forEach(sel => {
      this.querySelector(sel)?.addEventListener('change', async (e) => {
        const value = (e.target as HTMLInputElement | HTMLSelectElement).value;
        if (sel === '#cons-front') this.consolidation.serviceFrontId = value;
        if (sel === '#cons-start') this.consolidation.startDate = value;
        if (sel === '#cons-end') this.consolidation.endDate = value;
        this.consolidationModel = null;
        this.consolidationError = null;
        await this.refreshConsolidationScope();
        reopenConsolidationModal();
      });
    });

    this.querySelectorAll('input[name="cons-scope"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.consolidation.collaboratorScope = (e.target as HTMLInputElement).value as CollaboratorScope;
        if (this.consolidation.collaboratorScope === 'ALL') this.consolidation.professionalId = '';
        this.consolidationModel = null;
        this.consolidationError = null;
        reopenConsolidationModal();
      });
    });

    this.querySelector('#cons-professional')?.addEventListener('change', (e) => {
      this.consolidation.professionalId = (e.target as HTMLSelectElement).value;
      this.consolidationModel = null;
      this.consolidationError = null;
      reopenConsolidationModal();
    });

    const buildModel = async () => {
      this.consolidationError = null;
      this.consolidationModel = null;
      try {
        const model = await platform.rdoService.buildConsolidatedModel(this.getContext(), this.consolidationFilters());
        this.consolidationModel = model;
        return model;
      } catch (err: any) {
        // Erro real no console; mensagem específica na tela. Nada é mascarado.
        console.error('[RDO CONSOLIDADO] Falha ao montar o documento', err);
        this.consolidationError = err?.safeMessage || err?.message || 'Não foi possível montar o RDO Consolidado.';
        return null;
      }
    };

    // VISUALIZAR: mostra a mesma sequência Frente → Colaborador → Data → Registro do PDF.
    this.querySelector('#btn-preview-consolidado')?.addEventListener('click', async () => {
      await buildModel();
      reopenConsolidationModal();
    });

    // GERAR: o PDF usa exatamente os mesmos filtros já exibidos no preview.
    this.querySelector('#btn-generate-consolidado')?.addEventListener('click', async () => {
      const model = this.consolidationModel || await buildModel();
      if (!model) { reopenConsolidationModal(); return; }

      console.log("[REAL-PDF-FLOW]", {
        module: "RDO_CONSOLIDATED",
        stage: "HANDLER_START",
        handler: "generateConsolidado"
      });

      try {
        const snapshot = await platform.rdoService.buildConsolidatedSnapshotFromFilters(
          this.getContext(),
          this.consolidationFilters()
        );
        console.log("[REAL-PDF-FLOW]", {
          module: "RDO_CONSOLIDATED",
          stage: "SNAPSHOT_RECEIVED",
          createdAt: snapshot.createdAt,
          createdAtType: typeof snapshot.createdAt
        });
        const pdfViewer = this.querySelector('#rdo-pdf-preview') as any;
        const title = this.querySelector('#pdf-viewer-title') as HTMLElement | null;
        if (title) title.innerText = `RDO Consolidado • ${snapshot.data?.fileName || snapshot.documentNumber}`;
        if (pdfViewer) pdfViewer.snapshot = snapshot;
        this.querySelector('#modal-consolidado')?.classList.remove('active');
        this.querySelector('#modal-pdf-viewer')?.classList.add('active');
      } catch (err: any) {
        console.error('[RDO CONSOLIDADO] Falha ao gerar o PDF', err);
        this.consolidationError = err?.safeMessage || err?.message || 'Não foi possível gerar o PDF do RDO Consolidado.';
        reopenConsolidationModal();
      }
    });

    // INSPECT IA REVIEW — revisão de português da descrição (regras seguras:
    // nada é acrescentado nem inventado; números, TAGs e normas preservados).
    this.querySelector('#btn-ai-language-review')?.addEventListener('click', () => {
      const descInput = this.querySelector('#form-description') as HTMLTextAreaElement | null;
      if (!descInput) return;
      const suggestions = reviewLanguage(descInput.value);
      if (suggestions.length === 0) {
        alert("INSPECT IA REVIEW: nenhuma correção de português necessária.");
        return;
      }
      const list = suggestions.map(s => `• "${s.originalText}" → "${s.suggestedText}" (${s.explanation})`).join("\n");
      if (confirm(`INSPECT IA REVIEW — ${suggestions.length} correção(ões) segura(s):\n\n${list}\n\nAplicar à descrição?`)) {
        descInput.value = applyLanguageSuggestions(descInput.value, suggestions);
      }
    });
  }
}

if (!customElements.get("app-rdo-page")) {
  customElements.define("app-rdo-page", AppRdoPage);
}



